import React, { ReactNode, useEffect, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { Controller, useFieldArray, useForm } from "react-hook-form";
import toast from 'react-hot-toast';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { components } from 'react-select';

import { yupResolver } from '@hookform/resolvers/yup';
import { AsyncPaginate } from 'react-select-async-paginate';
import { QuickBookCOAClassificationFormI } from '../../../resources/form-props';
import { QuickBookCOAValidate } from '../../../resources/form-validator';
import { useBankFeedApi } from '../../../resources/hooks/api/bankFeedApiHook';
import { useChartAccountApi } from '../../../resources/hooks/api/chartAccountApiHook';
import { useReportApi } from '../../../resources/hooks/api/useReportApiHook';
import { getBranch } from '../../../store/user/selectors';
import { getIsQbdImport } from '../../../store/settings/selectors';

import type { SelectInstance } from 'react-select';
import ChartAccountModal from '../../../components/Modal/ChartOfAccount/ChartAccountModal';
import { SELECT_CUSTOM_STYLE, normalizeQBOAccountType, expectedGLRangeForQBOAccount } from '../../../resources/constants';
import { calculateMaxHeightContent } from '../../../resources/functions';
import { useThirdPartyApi } from '../../../resources/hooks/api/thirdPartyApiHook';
import QBBankRegistrationModal from './QBBankRegistrationModal';
import QBDReconciliationCutoffModal from './QBDReconciliationCutoffModal';
import './QBCOAClassification.css';


interface Props {

}

const HierarchyConnector = () => (
    <svg className="qbo-hierarchy-connector" viewBox="0 0 30 42" aria-hidden="true" focusable="false">
        <path d="M3 28 H30" />
    </svg>
);

type ClassificationType = 'Liability' | 'Asset' | 'Expense' | 'Revenue' | 'Equity' | 'CostOfGoodsSold' | 'OtherIncomeExpense';

const QB_ACCOUNT_TYPE_CREATE_RANGES: Record<string, { start_range: number, end_range: number, name: string }> = {
    otherincome: { start_range: 99860, end_range: 99890, name: 'OTHER INCOME' },
};

// How far above a to-be-promoted posting parent to look for its children's account
// numbers. The promoted header's range must cover its own number, every child, and a
// trailing total row, and must not swallow an unrelated account in between (enforced
// server-side in promote_to_header) — so a narrow window immediately above the parent
// is both what's needed and what's least likely to collide.
const PROMOTION_CHILD_RANGE_SPAN = 10;

const normalizeAccountName = (value: string = '') => value.toUpperCase().replace(/[^A-Z0-9]/g, '');
const POSTING_REPORT_CODES = new Set(['VP', 'IP']);
const hasUsableChildRange = (account: any) => !!account?.startRange && !!account?.endRange && (account.endRange > account.startRange);

type HierarchyAccount = {
    label: string;
    shortLabel: string;
    value: number;
    accountNum: number;
    parentId: number | null;
    reportCode: string;
    financialStatement: string;
    ancestorIds: number[];
    name: string;
    hasChildren: boolean;
    startRange?: number;
    endRange?: number;
    proposedName?: string;
    // Session-only: true for an account created during THIS page load.
    isNewAccount?: boolean;
    // Persisted equivalent, loaded from the server — survives a reload, which
    // isNewAccount cannot.
    isThirdPartyCreated?: boolean;
    isContainer?: boolean;
};

const formatAccountLabel = (option: any) => {
    if (option?.proposedName && option?.accountNum) {
        return i18next.t('option.promote', { ns: 'scenes.thirdparty.quickbook.QBCOAClassification', accountNum: option.accountNum, name: option.proposedName });
    }
    return option?.shortLabel || String(option?.label || '').split(' > ').pop();
};

const formatSelectedAccount = (option: any, meta: { context: string }) =>
    meta.context === 'value'
        ? formatAccountLabel(option)
        : option?.proposedName
            ? formatAccountLabel(option)
            : option?.label;

const HierarchyOption = (props: any) => {
    const name = formatAccountLabel(props.data) || '';
    return (
        <components.Option {...props}>
            {name}
        </components.Option>
    );
};

// 'first_valid' means only one thing: an unused structural slot will be RENAMED to
// the QBO account (the `proposedName` options from getParentOptions' fallback). That
// is the low-confidence case the amber row highlight exists to flag — nothing else
// should carry it.
// Fallback strip label for a QBO row with no Classification. Rare, but a blank strip
// heading reads as a rendering bug rather than as missing source data.
const UNCLASSIFIED_SECTION = 'Unclassified';

const isFirstValidMapping = (source?: string | null) => source === 'first_valid';
const isBackendGeneratedMapping = (source?: string | null) => (source === 'qbo_generated') || (source === 'qbo_generated_parent');

// Any mapping the system chose rather than the user. Covers 'matched' (resolved to a
// pre-existing account, by the import-time matcher or this screen's auto-fill),
// 'created' (an account generated for this row), 'parent' (parent resolved to an
// existing header) and 'first_valid' — everything except 'manual'. The review gate
// counted only 'first_valid', so e.g. 77 import-time 'matched' rows were attested to
// without ever being counted, even though those are exactly as machine-guessed as
// the rest.
const isAutoMapping = (row: any) => !!row?.account_id?.value && (row?.mapping_source !== 'manual');


// Classification display order
const CLASSIFICATION_ORDER: Record<string, number> = {
    'Asset': 0,
    'Liability': 1,
    'Equity': 2,
    'Revenue': 3,
    'Cost of Goods Sold': 4,
    'Expense': 5,
    'Other Income': 6,
    'Other Expense': 7,
};

/**
 * Sorts QBO accounts so that:
 *  1. Classifications appear in a logical financial-statement order
 *     (Asset → Liability → Equity → Revenue → COGS → Expense → Other)
 *  2. Within a classification, top-level accounts are grouped by AccountType then name
 *  3. Children are inserted recursively immediately after their parent at any depth,
 *     so a 3-level tree like A → B → C renders as A, B, C in order.
 */
const sortQBOAccounts = (accounts: any[]): any[] => {
    // Group all children by their parent QBO Id
    const childrenByParent: Record<string, any[]> = {};
    accounts.forEach(a => {
        const parentId = a['ParentRef_value'];
        if (parentId) {
            if (!childrenByParent[parentId]) childrenByParent[parentId] = [];
            childrenByParent[parentId].push(a);
        }
    });

    // Sort siblings alphabetically within each parent
    Object.values(childrenByParent).forEach(siblings =>
        siblings.sort((a, b) =>
            (a['FullyQualifiedName'] || '').localeCompare(b['FullyQualifiedName'] || '')
        )
    );

    // Recursively append an account then all its descendants (depth-first)
    const appendWithDescendants = (result: any[], account: any) => {
        result.push(account);
        const kids = childrenByParent[account['Id_qbo']];
        if (kids) {
            kids.forEach(kid => appendWithDescendants(result, kid));
        }
    };

    // Sort top-level accounts: classification order first, then AccountType, then name
    const topLevel = accounts.filter(a => !a['ParentRef_value']);
    topLevel.sort((a, b) => {
        const classA = CLASSIFICATION_ORDER[a['Classification']] ?? 99;
        const classB = CLASSIFICATION_ORDER[b['Classification']] ?? 99;
        if (classA !== classB) return classA - classB;

        const typeA = (a['AccountType'] || '').toLowerCase();
        const typeB = (b['AccountType'] || '').toLowerCase();
        if (typeA !== typeB) return typeA.localeCompare(typeB);

        return (a['FullyQualifiedName'] || '').localeCompare(b['FullyQualifiedName'] || '');
    });

    // Flatten recursively
    const result: any[] = [];
    topLevel.forEach(account => appendWithDescendants(result, account));

    // Append any orphans (ParentRef_value points to an Id not in the list)
    const placed = new Set(result.map(a => a['Id_qbo']));
    accounts.forEach(a => {
        if (!placed.has(a['Id_qbo'])) result.push(a);
    });

    return result;
};

const getSectionIcon = (name: string) => {
    const key = (name || '').toLowerCase();
    if (key.includes('asset')) return '🏦';
    if (key.includes('liab')) return '💳';
    if (key.includes('equity')) return '⚖️';
    if (key.includes('revenue') || key.includes('income')) return '📈';
    if (key.includes('expense')) return '💸';
    if (key.includes('cogs') || key.includes('cost')) return '📦';
    return '📂';
};

const modernSelectStyle = {
    control: (provided: any, state: any) => ({
        ...provided,
        backgroundColor: '#ffffff',
        borderColor: state.isFocused ? '#6366f1' : '#cbd5e1',
        borderRadius: '10px',
        minHeight: '38px',
        height: '38px',
        fontSize: '13px',
        fontWeight: 500,
        boxShadow: state.isFocused ? '0 0 0 3px rgba(99, 102, 241, 0.15)' : 'none',
        transition: 'all 0.2s ease',
        '&:hover': {
            borderColor: '#6366f1',
        },
    }),
    valueContainer: (provided: any) => ({
        ...provided,
        padding: '0 12px',
        height: '36px',
    }),
    input: (provided: any) => ({
        ...provided,
        margin: '0',
        padding: '0',
    }),
    indicatorsContainer: (provided: any) => ({
        ...provided,
        height: '36px',
    }),
    dropdownIndicator: (provided: any) => ({
        ...provided,
        padding: '4px 8px',
        color: '#94a3b8',
        '&:hover': {
            color: '#6366f1',
        },
    }),
    clearIndicator: (provided: any) => ({
        ...provided,
        padding: '4px 8px',
        color: '#94a3b8',
    }),
    singleValue: (provided: any) => ({
        ...provided,
        color: '#0f172a',
        fontWeight: 500,
    }),
    placeholder: (provided: any) => ({
        ...provided,
        color: '#94a3b8',
        fontSize: '13px',
    }),
    menu: (provided: any) => ({
        ...provided,
        borderRadius: '12px',
        boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.15), 0 4px 6px -2px rgba(15, 23, 42, 0.05)',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        zIndex: 9999,
        marginTop: '4px',
    }),
    menuPortal: (provided: any) => ({
        ...provided,
        zIndex: 9999,
    }),
    menuList: (provided: any) => ({
        ...provided,
        padding: '4px',
        maxHeight: '220px',
    }),
    option: (provided: any, state: any) => ({
        ...provided,
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: state.isSelected ? 600 : 400,
        backgroundColor: state.isSelected
            ? '#6366f1'
            : state.isFocused
                ? '#f1f5f9'
                : 'transparent',
        color: state.isSelected ? '#ffffff' : '#0f172a',
        cursor: 'pointer',
        padding: '8px 12px',
        margin: '2px 0',
        '&:active': {
            backgroundColor: '#e0e7ff',
        },
    }),
};

const QBCOAClassification: React.FC<Props> = () => {

    const { fetchQuickBookAccountCharts, postQuickBookAccountCharts, fetchQboClassificationMap, matchQboParent, quickBookImportData } = useThirdPartyApi();
    const { glAccountsList, chartAvailableAccounts, createChart, getAccountLebel, getListTree } = useChartAccountApi();
    const { addBankAccounts } = useBankFeedApi();
    const { regenerateBalanceSheet } = useReportApi();
    const navigate = useNavigate();
    const branchData = useSelector(getBranch)
    const _isQbdImport = useSelector(getIsQbdImport);
    const { t } = useTranslation("scenes.thirdparty.quickbook.QBCOAClassification");

    const [records, setRecords] = useState<any>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isImporting, setIsImporting] = useState<boolean>(false);
    const [isAutoFilling, setIsAutoFilling] = useState<boolean>(false);

    const handleImportCOA = () => {
        if (!branchData?.id) return;
        setIsImporting(true);
        quickBookImportData(
            { branch_id: +branchData['id'], step: 1 },
            (message: string) => {
                setIsImporting(false);
                toast.success(message || t('toast.importSuccess'));
                fetchCAList(fetchAllGlAccounts);
            },
            (message: string) => {
                setIsImporting(false);
                toast.error(message || t('toast.importFailed'));
            }
        );
    };
    const [contentMaxHeight, setContentMaxHeight] = useState<number>(0);
    const theadRef = useRef<HTMLTableSectionElement>(null);
    const [stripTop, setStripTop] = useState<number>(0);

    const [selectCustomStyle] = useState<any>(SELECT_CUSTOM_STYLE('#d1ffbd', '#dfe34d'));
    const checkAllRef = useRef<HTMLInputElement>(null);
    const glAccountsCacheRef = useRef<{ label: string; value: number; accountNum: number }[]>([]);
    const hierarchyAccountsRef = useRef<HierarchyAccount[]>([]);
    const hierarchyAccountByIdRef = useRef<Record<number, HierarchyAccount>>({});
    const [glAccountsLoaded, setGlAccountsLoaded] = useState<boolean>(false);
    const [parentAccountsLoaded, setParentAccountsLoaded] = useState<boolean>(false);
    const [classificationMapLoaded, setClassificationMapLoaded] = useState<boolean>(false);
    const qboClassificationMapRef = useRef<{
        by_account_type: Record<string, { anchors?: number[]; posting_range?: [number, number] }>;
        by_account_sub_type: Record<string, number[]>;
        by_account_sub_type_range?: Record<string, [number, number]>;
        by_name_range?: Record<string, [number, number]>;
        by_classification: Record<string, [number, number]>;
    }>({ by_account_type: {}, by_account_sub_type: {}, by_classification: {} });
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [showBankRegModal, setShowBankRegModal] = useState<boolean>(false);
    // QBD import only -- shown once, right after COA mapping submits successfully,
    // to backfill bank reconciliation up to a user-attested "reconciled through"
    // month. Never set for a QBO import (see the _isQbdImport check where this is
    // triggered, in submitAccountChartData).
    const [showReconciliationCutoffModal, setShowReconciliationCutoffModal] = useState<boolean>(false);
    const [pendingBankRows, setPendingBankRows] = useState<any[]>([]);
    const [pendingSubmitData, setPendingSubmitData] = useState<any>(null);
    // Payload parked between the dry-run preview and the real commit. See postData:
    // the commit cannot be dispatched from inside the dry run's own saga callback.
    const [pendingPromotionConfirm, setPendingPromotionConfirm] = useState<any>(null);
    const [savedBankDetails, setSavedBankDetails] = useState<any[]>([]);
    const [bankSelectionError, setBankSelectionError] = useState<boolean>(false);
    const [createModalRowIndex, setCreateModalRowIndex] = useState<number>(-1);
    const [createModalAccountName, setCreateModalAccountName] = useState<string>('');
    const [createModalSection, setCreateModalSection] = useState<{ start_range: number, end_range: number, name: string } | null>(null);
    const [createModalParentAccountIn, setCreateModalParentAccountIn] = useState<number[] | null>(null);
    const [createModalForcedParentId, setCreateModalForcedParentId] = useState<number | null>(null);
    const [parentDetailsRecord, setParentDetailsRecord] = useState<any>(null);
    const [parentDetailsAccountOptions, setParentDetailsAccountOptions] = useState<any[]>([]);
    const [accountListVersion, setAccountListVersion] = useState<number>(0);
    const [activeInvalidIndex, setActiveInvalidIndex] = useState<number | null>(null);
    const [validationActivated, setValidationActivated] = useState<boolean>(false);
    const [missingValidationIndexes, setMissingValidationIndexes] = useState<number[]>([]);
    const [autoMappingsReviewed, setAutoMappingsReviewed] = useState<boolean>(false);
    const [activeFilterTab, setActiveFilterTab] = useState<'all' | 'matched' | 'new' | 'review' | 'pending'>('all');
    const [searchQuery, setSearchQuery] = useState<string>('');
    const autoFillKeyRef = useRef<string>('');

    const { register, trigger, control, handleSubmit, reset, setValue, getValues, setError, clearErrors, watch, formState: { errors } } = useForm<QuickBookCOAClassificationFormI>({
        defaultValues: {
            coa: []
        },
        resolver: yupResolver(QuickBookCOAValidate)
    });
    const { fields, remove: formRemove, replace: formReplace } = useFieldArray({
        control,
        name: "coa"

    });

    // Watch the entire `coa` array
    const coaValues = watch('coa');

    // Was this row's account generated for it, rather than picked out of the existing
    // chart? Single definition, shared with the row's own "New" badge — the two must
    // never disagree about what counts as newly created.
    const isNewlyCreatedMapping = (row: any) => {
        const selection = row?.account_id;
        if (!selection?.value) return false;
        const account = hierarchyAccountByIdRef.current[+selection.value];
        return !!(isBackendGeneratedMapping(row?.mapping_source) || selection.isNewAccount || account?.isNewAccount || account?.isThirdPartyCreated);
    };

    // Split, because the two carry completely different risk. A newly created account
    // was generated FROM the QBO name, so it cannot be a false match — there is nothing
    // to review. A matched one is the matcher's guess against a pre-existing account,
    // which is exactly where wrong mappings come from. Only the matched ones gate submit;
    // folding creations in would pad the number the user is attesting to with rows that
    // are correct by construction.
    const autoMatchedCount = (coaValues || []).filter((row: any) => isAutoMapping(row) && !isNewlyCreatedMapping(row)).length;
    const autoCreatedCount = (coaValues || []).filter((row: any) => isAutoMapping(row) && isNewlyCreatedMapping(row)).length;
    const reviewNeededCount = (coaValues || []).filter((row: any) => !row?.account_id?.value || isFirstValidMapping(row?.mapping_source)).length;
    // Per-classification tallies for the section strips. Rows are already sorted by
    // CLASSIFICATION_ORDER, so a strip is emitted wherever the classification changes
    // on the way down — no regrouping, and nothing moves when a mapping is edited.
    const sectionSummaries: Record<string, { total: number; matched: number; created: number; blank: number }> =
        (coaValues || []).reduce((acc: any, row: any) => {
            const key = row?.classification || UNCLASSIFIED_SECTION;
            const entry = acc[key] || (acc[key] = { total: 0, matched: 0, created: 0, blank: 0 });
            entry.total += 1;
            if (!row?.account_id?.value) entry.blank += 1;
            else if (isNewlyCreatedMapping(row)) entry.created += 1;
            else if (isAutoMapping(row)) entry.matched += 1;
            return acc;
        }, {});

    const flattenAccounts = (items: any[] = [], ancestorIds: number[] = [], path: string[] = []): HierarchyAccount[] =>
        items.flatMap((item: any) => {
            const children = item.child || item.children || [];
            const value = +item.id;
            const shortLabel = `${item.name}(${item.account})`;
            const account: HierarchyAccount = {
                label: [...path, shortLabel].join(' > '),
                shortLabel,
                value,
                accountNum: parseInt(item.account, 10) || 0,
                parentId: item.parent_id ? +item.parent_id : null,
                reportCode: item.report_code || '',
                financialStatement: item.financial_statement || '',
                ancestorIds,
                name: item.name || '',
                hasChildren: children.length > 0,
                startRange: item.start_range || undefined,
                endRange: item.end_range || undefined,
                isThirdPartyCreated: !!item.is_third_party_created,
            };
            return [account, ...flattenAccounts(children, [...ancestorIds, value], [...path, shortLabel])];
        });

    const getCreateRangeForRow = (row: any) => {
        const accountType = normalizeQBOAccountType(row?.accountType || row?.AccountType || '');
        const accountName = String(row?.name || row?.FullyQualifiedName || row?.NAME || '').trim();
        if (/^other\s+income$/i.test(accountName)) {
            return QB_ACCOUNT_TYPE_CREATE_RANGES.otherincome;
        }
        return QB_ACCOUNT_TYPE_CREATE_RANGES[accountType] || null;
    };

    const getCreateParentForRow = (row: any, rows: any[] = getValues('coa') || []) => {
        if (!row?.isChild) return null;

        const mappedParentId = getMappedQboParentId(row, rows);
        const mappedParent = hierarchyAccountByIdRef.current[+mappedParentId];
        if (mappedParent) return mappedParent;

        const rowIndex = rows.findIndex((item: any) => (item === row) || (String(item.Id_qbo) === String(row.Id_qbo)));
        const parentRow = rows.find((item: any) => String(item.Id_qbo) === String(row.parentQboId))
            || rows.slice(0, Math.max(rowIndex, 0)).reverse().find((item: any) => item.isParent);
        const selection = parentRow?.account_id;
        if (!selection?.value) return null;

        return {
            value: +selection.value,
            accountNum: selection.accountNum || parseInt(String(selection.shortLabel || selection.label || '').match(/\((\d+)\)/)?.[1] || '', 10) || 0,
            name: selection.proposedName || selection.name || String(selection.shortLabel || selection.label || '').split('(')[0].trim(),
            startRange: selection.startRange,
            endRange: selection.endRange,
        };
    };

    const getCreateSectionForRow = (row: any, mappedParent: any) => {
        if (hasUsableChildRange(mappedParent)) {
            return {
                start_range: mappedParent!.startRange + 1,
                end_range: mappedParent!.endRange,
                name: mappedParent!.name,
            };
        }

        if (mappedParent?.accountNum) {
            return {
                start_range: mappedParent.accountNum + 1,
                end_range: mappedParent.accountNum + PROMOTION_CHILD_RANGE_SPAN,
                name: mappedParent.name,
            };
        }

        return getCreateRangeForRow(row);
    };

    const isNewOrPromotedParentSelection = (selection: any, selectedHierarchy: any) =>
        !!selection?.value && !!(
            selection?.isNewAccount
            || selectedHierarchy?.isNewAccount
            || selection?.proposedName
            || (selectedHierarchy?.reportCode && POSTING_REPORT_CODES.has(selectedHierarchy.reportCode))
        );


    // A QBO child is only mappable once its parent has resolved to *some* RevRex
    // account — that's what gives the child a scope to allocate within, either the
    // parent's real range or (for a leaf still awaiting promotion) the deterministic
    // window just above the parent's own number. With no parent at all there is no
    // scope, and the child would fall back to the whole section and land anywhere.
    //
    // Deliberately NOT gated on hasUsableChildRange: nothing on this screen promotes
    // server-side (promotion happens at submit, in map_account_charts), so a
    // promotion-pending parent never gains a real range while the screen is open.
    // Gating on the range would leave those children permanently blank.
    const isChildWithUnresolvedParent = (row: any, rows: any[] = getValues('coa') || []) =>
        !!row?.isChild && !getMappedQboParentId(row, rows);

    const isPendingRowAction = (row: any, index: number, rows: any[] = getValues('coa') || []) => {
        const selection = row?.account_id;
        if (!selection?.value) return true;
        const isParent = !!(fields[index]?.isParent || row?.isParent);
        if (!isParent) return isFirstValidMapping(row?.mapping_source);

        // A parent is pending only while its target still has to become a header —
        // i.e. the user manually picked a posting account, or a brand new one is
        // proposed. A backend-generated parent already points at a real GH/SH/SSH with
        // its PARENT ACTIVITY child in place, so there is nothing left to act on; it
        // used to be counted here purely to force the (now removed) Save Parent click,
        // which meant every generated parent blocked submit forever.
        const selectedHierarchy = hierarchyAccountByIdRef.current[+(selection?.value || 0)];
        return isNewOrPromotedParentSelection(selection, selectedHierarchy);
    };

    // Must stay BELOW isPendingRowAction.
    const pendingActionCount = (coaValues || []).filter((row: any, index: number) => {
        const liveRow = { ...(fields[index] as any), ...(row || {}) };
        return isPendingRowAction(liveRow, index, coaValues || []);
    }).length;

    // QBO AccountType/Classification -> RevRex section anchors, from the backend's
    // single source (get_qbo_classification_map) rather than a separately-maintained
    // frontend copy — the two had drifted (see plan Task 7).
    const getParentAccountsForRow = (classification: ClassificationType | string = 'Liability', accountType: string = '', accountName: string = '', accountSubType: string = ''): number[] => {
        const map = qboClassificationMapRef.current;
        // Name-based override: QBO sometimes sends "Other Income" accounts with AccountType="Income"
        // instead of "OtherIncome", so we check the name to force correct mapping.
        if (accountName && /^other\s+income$/i.test(accountName.trim())) {
            return map.by_account_type['otherincome']?.anchors || [];
        }
        // Name-based override, mirroring classify_to_grand_header's NAME_OVERRIDE
        // (gl-api classification_map.py): QBO often files these under a generic
        // catch-all AccountSubType (e.g. "OtherMiscellaneousServiceCost") instead of
        // the specific one below, so a "Taxes Paid" row's real match (correctly
        // resolved into its own 9929-9940 section by the backend) got flagged here as
        // "outside the expected section" — this check didn't know that section
        // existed, since it only ever consulted AccountSubType/AccountType/
        // Classification, never the name. Checked before subtype for the same reason
        // as the backend: an exact name hit beats a subtype known to be generic.
        const normalizedName = normalizeQBOAccountType(accountName);
        const normalizedType = normalizeQBOAccountType(accountType || classification);
        const isAssetType = ['asset', 'bank', 'fixedasset', 'othercurrentasset', 'otherasset', 'accountsreceivable'].includes(normalizedType);
        const NAME_TO_SUBTYPE_KEY: Record<string, string> = {
            taxespaid: 'taxespaid',
            interest: 'interestpaid',
            depreciation: 'depreciation',
            amortization: 'amortization',
            gainlossform4797: 'gainlossonsaleoffixedassets',
        };
        const nameSubtypeKey = (!isAssetType && NAME_TO_SUBTYPE_KEY[normalizedName])
            || (!isAssetType && normalizedName.includes('depreciation') && !normalizedName.includes('accumulated') ? 'depreciation' : '')
            || (!isAssetType && normalizedName.includes('amortization') && !normalizedName.includes('accumulated') ? 'amortization' : '');
        const byName = nameSubtypeKey && (map.by_account_sub_type || {})[nameSubtypeKey];
        if (byName) return byName;
        // Subtype-level anchor first — "Expense" alone covers Advertising through
        // Interest Paid through Taxes Paid; only AccountSubType tells them apart.
        // Only covers the subtypes classify_to_grand_header explicitly overrides
        // (interest/taxes/depreciation/amortization/etc) — an uncontested subset,
        // see Task 18. Everything else still falls through to AccountType below,
        // unchanged from before this subtype table existed.
        const bySubType = (map.by_account_sub_type || {})[normalizeQBOAccountType(accountSubType)];
        if (bySubType) return bySubType;
        const byType = map.by_account_type[normalizeQBOAccountType(accountType)]?.anchors;
        if (byType) return byType;

        // Classification-level fallback, for a QBO AccountType the anchor table
        // doesn't cover. The backend serves this level as a range rather than
        // discrete anchors, so take the range START — that's the grand header's own
        // account number, which is exactly the anchor the callers want (they test
        // `preferredNumbers.includes(account.accountNum)` against header rows).
        // Returning nothing here would drop scoping entirely and fall back to
        // "any account on this financial statement", which offers a whole statement.
        const classificationRange = map.by_classification[(classification || 'Liability').toString().toLowerCase()];
        return classificationRange ? [classificationRange[0]] : [];
    };

    const getMappedQboParentId = (row: any, rows: any[] = getValues('coa') || []) => {
        if (!row?.parentQboId) return null;
        return rows.find((item: any) => String(item.Id_qbo) === String(row.parentQboId))?.account_id?.value || null;
    };

    const accountOwnsAnchor = (account: HierarchyAccount, anchorAccountNum: number) => {
        const anchor = hierarchyAccountsRef.current.find(candidate => candidate.accountNum === anchorAccountNum);
        return !!anchor && ((anchor.value === account.value) || (anchor.ancestorIds || []).includes(account.value));
    };

    // Resolves name/subtype overrides before the AccountType band, matching both the
    // backend and getParentAccountsForRow above. AccountType alone put Depreciation
    // (Expense-typed, but parented into 99410-99650) in the SG&A band and warned that a
    // correct mapping was out of section.
    const getPostingRangeForRow = (row: any): [number, number] | null =>
        expectedGLRangeForQBOAccount(
            row?.accountType || row?.AccountType || '',
            row?.accountSubType || row?.AccountSubType || '',
            row?.name || row?.NAME || row?.Name || '',
            qboClassificationMapRef.current,
        );

    const isReusableContainerSelection = (row: any) => {
        const selection = row?.account_id;
        if (!row?.isParent || !selection?.value || selection?.proposedName || selection?.isNewAccount) return false;
        const account = hierarchyAccountByIdRef.current[+selection.value];
        const reportCode = selection.reportCode || account?.reportCode;
        return ['GH', 'SH', 'SSH'].includes(reportCode) && !!(selection.isContainer || account?.hasChildren);
    };

    const countQboChildRows = (row: any, rows: any[] = getValues('coa') || []) =>
        rows.filter((item: any) => item?.parentQboId && (String(item.parentQboId) === String(row?.Id_qbo))).length;

    // Mirror of the backend's has_clean_child_span. A posting leaf can only be promoted
    // if it can own the numeric span its children will sit in, so the next childCount+1
    // numbers must be free — the first FOREIGN occupied number after it caps everything
    // (8306 can't host any child because 8307 is taken by an unrelated account). Its own
    // existing children are exempt: they belong inside the span, and are usually there
    // because an earlier attempt created them before failing further along.
    const canHostChildSpan = (account: any, childCount: number) => {
        if (childCount <= 0) return true;
        const ownChildNumbers = new Set(hierarchyAccountsRef.current
            .filter(candidate => candidate.parentId === account.value)
            .map(candidate => candidate.accountNum));
        const taken = new Set(hierarchyAccountsRef.current
            .map(candidate => candidate.accountNum)
            .filter(num => !ownChildNumbers.has(num)));
        for (let num = account.accountNum + 1; num <= (account.accountNum + childCount + 2); num += 1) {
            if (taken.has(num)) return false;
        }
        return true;
    };

    const getParentOptions = (row: any, rows: any[] = getValues('coa') || []) => {
        const mappedParentId = getMappedQboParentId(row, rows);
        const statement = ['Asset', 'Liability', 'Equity'].includes(row.classification || row.Classification) ? 'BS' : 'PL';
        const preferredNumbers = getParentAccountsForRow(
            row.classification || row.Classification,
            row.accountType || row.AccountType,
            row.name || row.FullyQualifiedName || row.NAME || '',
            row.accountSubType || row.AccountSubType
        );
        const scopeRootIds = hierarchyAccountsRef.current
            .filter(account => preferredNumbers.includes(account.accountNum))
            .map(account => account.value);
        const structuralOptions = hierarchyAccountsRef.current
            // QBO parent rows must map to RevRex headers (SH/SSH) only, never a grand
            // header (GH): a GH is the section's own structural root — the backend
            // (find_gl_account_for_qbo_parent) already refuses to match/rename one for
            // exactly this reason. Without this exclusion, e.g. SG&A's own GH(8000) was
            // offered here as a reusable "Payroll Expenses" slot; renaming it would have
            // orphaned every other account in the section.
            .filter(account => (account.reportCode === 'SH') || (account.reportCode === 'SSH'))
            .filter(account => {
                if (mappedParentId) return account.parentId === +mappedParentId;
                if (scopeRootIds.length > 0) {
                    return scopeRootIds.includes(account.value)
                        || scopeRootIds.includes(account.parentId || 0);
                }
                return account.financialStatement === statement;
            })
            // preferredNumbers only SCOPES which section is eligible (above); within that
            // section, order is purely ascending account number — no reordering by a
            // hardcoded "preferred" list, so a genuinely lower/available account is never
            // skipped over a higher one.
            .sort((a, b) => a.accountNum - b.accountNum);

        const qboName = (row.name || row.FullyQualifiedName || row.NAME || '').split(':').pop() || '';
        const usedIds = new Set(rows
            .filter(item => String(item?.Id_qbo) !== String(row?.Id_qbo))
            .filter(item => !isReusableContainerSelection(item))
            .map(item => item?.account_id?.value)
            .filter(Boolean)
            .map(Number));
        const exactMatches = structuralOptions.filter(option =>
            (normalizeAccountName(option.name) === normalizeAccountName(qboName))
            && !usedIds.has(option.value));
        if (exactMatches.length > 0) return exactMatches;

        // Reuse one unused structural parent slot (first available, ascending account
        // number, already scoped to the correct section above). The API renames it to
        // the QBO parent on submit, preserving the valid RevRex hierarchy and child range.
        // Must be genuinely empty (hasChildren === false): a header that already has
        // real children is an occupied section, not a free slot — e.g. FIXED
        // ASSETS(2001, SSH) already parents MEDICAL EQUIPMENT and others; renaming it
        // to "Furniture and Equipment" would relabel that whole section and every
        // unrelated account under it, not create/reuse a slot for furniture.
        if (!qboName) return [];
        const freeSlots = structuralOptions.filter(option => !usedIds.has(option.value) && !option.hasChildren);
        // Every header in this section is already occupied — offer childless posting
        // leaves instead, which the submit path promotes VP/IP -> SSH (promote_to_header).
        // The backend matcher resolves parents to exactly these too (needs_promotion),
        // so without them the dropdown could come back empty for a section that is
        // simply full (e.g. Fixed Assets, whose only header 2001 has children), leaving
        // no way to map the row by hand at all.
        const promotable = freeSlots.length > 0 ? [] : hierarchyAccountsRef.current
            .filter(account => POSTING_REPORT_CODES.has(account.reportCode)
                && !account.hasChildren
                && !usedIds.has(account.value)
                && (hierarchyAccountByIdRef.current[account.parentId || 0]?.reportCode !== 'SSH')
                && canHostChildSpan(account, countQboChildRows(row, rows))
                && (scopeRootIds.length > 0
                    ? scopeRootIds.includes(account.parentId || 0) || (account.ancestorIds || []).some(id => scopeRootIds.includes(id))
                    : account.financialStatement === statement))
            .sort((a, b) => a.accountNum - b.accountNum);
        return [...freeSlots, ...promotable]
            .map(slot => {
                const path = slot.label.split(' > ');
                path[path.length - 1] = qboName;
                return {
                    ...slot,
                    name: qboName,
                    label: path.join(' > '),
                    shortLabel: `${qboName} (${slot.accountNum})`,
                    proposedName: qboName,
                };
            });
    };

    // Warn (don't block) when the chosen RevRex account sits outside the section its
    // QBO AccountType belongs to — e.g. a Cost-of-Goods-Sold account mapped to an
    // expense (8xxx) leaf. Include the expected root so the user knows where to pick from.
    const getSectionMismatchWarning = (row: any) => {
        const selectedId = row?.account_id?.value;
        if (!selectedId) return null;
        if (isBackendGeneratedMapping(row?.mapping_source)) return null;
        if (row?.isParent && row?.account_id?.proposedName) return null;
        const acct = hierarchyAccountByIdRef.current[+selectedId];
        if (!acct) return null;

        // A child's real "expected section" is wherever its own QBO parent actually
        // resolved to — not a fresh classification/subtype guess independent of that.
        // Recomputing independently made this warn on every child of a parent placed
        // in one of the narrow subtype-scoped sections (taxes_paid, interest_net, ...):
        // e.g. "County Tax" has the same generic AccountSubType as "Taxes Paid" itself,
        // so its own guess fell back to the SG&A default even though it correctly sits
        // under Taxes Paid's real section (9929-9940).
        if (row?.isChild) {
            const mappedParentId = getMappedQboParentId(row);
            if (mappedParentId) {
                const inParentSection = (+selectedId === +mappedParentId)
                    || (acct.ancestorIds || []).includes(+mappedParentId);
                return inParentSection ? null : {
                    type: row.accountType || row.AccountType || '',
                    parents: hierarchyAccountByIdRef.current[+mappedParentId]?.label || '',
                };
            }
        }

        const createRange = getCreateRangeForRow(row);
        const preferred = getParentAccountsForRow(
            row.classification || row.Classification,
            row.accountType || row.AccountType,
            row.name || row.FullyQualifiedName || row.NAME || '',
            row.accountSubType || row.AccountSubType
        );
        const scopeRoots = hierarchyAccountsRef.current
            .filter(account => preferred.includes(account.accountNum));
        const scopeRootIds = scopeRoots.map(account => account.value);
        if (scopeRootIds.length === 0) return null; // can't determine the section → don't warn
        const inSection = scopeRootIds.includes(acct.value)
            || (acct.ancestorIds || []).some(id => scopeRootIds.includes(id));
        const postingRange = getPostingRangeForRow(row);
        if (postingRange && (acct.accountNum >= postingRange[0]) && (acct.accountNum <= postingRange[1])) return null;
        if (row?.isParent && ['GH', 'SH', 'SSH'].includes(acct.reportCode || '')) {
            const ownsExpectedAnchor = preferred.some(anchor => accountOwnsAnchor(acct, anchor));
            if (ownsExpectedAnchor) return null;
        }
        if (inSection) return null;
        return {
            type: row.accountType || row.AccountType || '',
            parents: createRange
                ? `${createRange.name} (${createRange.start_range}-${createRange.end_range})`
                : scopeRoots.map(account => account.shortLabel).join(', '),
        };
    };

    const getPreferredParentAccount = (row: any, rows: any[]) => {
        const usedIds = new Set(rows.map(item => item?.account_id?.value).filter(Boolean).map(Number));
        rows.filter(item => isReusableContainerSelection(item)).forEach(item => usedIds.delete(+item.account_id.value));
        const options = getParentOptions(row, rows)
            .filter(option => !option.proposedName && !usedIds.has(option.value));
        const saved = hierarchyAccountByIdRef.current[+row?.account_chart?.id];
        if (saved && options.some(option => option.value === saved.value)) return saved;

        const qboName = (row.FullyQualifiedName || row.NAME || '').split(':').pop() || '';
        const nameMatch = options.find(option => normalizeAccountName(option.name) === normalizeAccountName(qboName));
        if (nameMatch) return nameMatch;

        // No exact/saved match: take the first available option in the (already
        // section-scoped, ascending-by-account-number) dropdown list — no hardcoded
        // "preferred number" list involved in picking which one.
        return options[0] || null;
    };

    useEffect(() => {
        _handleDuplicateValidation();
    }, [JSON.stringify(coaValues), setError, clearErrors]);

    const _handleDuplicateValidation = (): boolean => {
        const selectedAccountIds = new Set();
        let hasError = false;

        // Iterate through the `coa` array to check for duplicates
        coaValues?.forEach((item, index) => {
            if (item?.account_id?.value) {
                if (selectedAccountIds.has(item.account_id.value)) {
                    // Set an error if a duplicate is found
                    if (!isReusableContainerSelection(item)) {
                        setError(`coa.${index}.account_id`, {
                            type: 'manual',
                            message: t('duplicate.error'),
                        });
                        hasError = true;
                    }
                } else {
                    // Clear the error if no duplicate is found
                    clearErrors(`coa.${index}.account_id`);
                    if (!isReusableContainerSelection(item)) {
                        selectedAccountIds.add(item.account_id.value);
                    }
                }
            }
        });

        return hasError;
    }

    useEffect(() => {
        setContentMaxHeight(calculateMaxHeightContent(131));
    })

    // The section strips stick BELOW the (already sticky) thead, so they need its real
    // height. Measured rather than hardcoded: the thead is two rows whose height moves
    // with font size, zoom and i18n line wrapping, and a stale constant would either
    // overlap the header or leave a gap the rows scroll through.
    useEffect(() => {
        const measure = () => {
            const height = theadRef.current?.getBoundingClientRect().height;
            if (height) setStripTop(Math.round(height) - 1);
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [fields.length, contentMaxHeight]);
    useEffect(() => {
        // Sequential, not parallel. Loading the QBO COA list is not a read: the backend's
        // list endpoint runs ensure_generated_qbo_mappings, which CREATES the GL accounts
        // the rows are mapped to. Fetching the chart alongside it raced that creation, so
        // the newest accounts were missing from hierarchyAccountByIdRef — and a row whose
        // mapped account isn't in that map fails the validPostingAccount check below and
        // renders as an empty dropdown / "Needs Review" despite being mapped server-side.
        // The tail of the allocation order (Expense, 8000s) was hit hardest.
        if (!branchData?.id) return;
        fetchCAList(fetchAllGlAccounts);
    }, [branchData?.id])

    useEffect(() => {
        if (!parentAccountsLoaded || !classificationMapLoaded) return;

        const selectedAccountIds = new Set<number>();
        const qboParentIds = new Set(records.map((record: any) => record['ParentRef_value']).filter(Boolean).map(String));
        const preparedRows: any[] = [];
        records.forEach((record: any, index: number) => {
            const preSelectedAccount = record['account_chart'];
            const preSelectedAccountId = preSelectedAccount?.id ? +preSelectedAccount.id : null;
            const isParent = qboParentIds.has(String(record['Id_qbo']));
            const isGenerated = isBackendGeneratedMapping(record['mapping_source']);
            const savedHierarchyAccount = preSelectedAccountId ? hierarchyAccountByIdRef.current[preSelectedAccountId] : null;
            const isSavedParentContainer = isParent && preSelectedAccountId && (
                (savedHierarchyAccount && ['GH', 'SH', 'SSH'].includes(savedHierarchyAccount.reportCode)) ||
                (preSelectedAccount?.reportCode && ['GH', 'SH', 'SSH'].includes(preSelectedAccount.reportCode)) ||
                !!preSelectedAccount
            );
            const rowContext = {
                ...record,
                name: record['FullyQualifiedName'],
                accountType: record['AccountType'],
                accountSubType: record['AccountSubType'],
                classification: record['Classification'],
                parentQboId: record['ParentRef_value'] || null,
            };
            const parentAccount = (isParent && !isGenerated && !isSavedParentContainer) ? getPreferredParentAccount(rowContext, preparedRows) : null;
            const mappedParentId = getMappedQboParentId(rowContext, preparedRows);
            // A child's saved mapping comes from the import-time matcher, which matches
            // by name with no parent awareness — so QBO "Professional Fees:Accounting"
            // arrives pre-mapped to ACCOUNTING(8001), an account outside its parent
            // entirely. The descendant check below catches that once the parent is
            // known, but `!mappedParentId` short-circuited it to valid whenever the
            // parent hadn't resolved yet, which is exactly when a bad child mapping
            // slips through. Treat "child with no resolved parent" as not-yet-valid
            // instead. Non-hierarchical rows are unaffected.
            // NB: mappedParentId comes from preparedRows above, not getValues — the
            // form isn't populated yet at this point.
            const isChildRow = !!record['ParentRef_value'];
            const validPostingAccount = savedHierarchyAccount
                && POSTING_REPORT_CODES.has(savedHierarchyAccount.reportCode)
                && (isChildRow ? !!mappedParentId : true)
                && (!mappedParentId || savedHierarchyAccount.ancestorIds.includes(+mappedParentId));
            const account_id = ((isGenerated || isSavedParentContainer) && preSelectedAccountId) ? {
                label: preSelectedAccount['name'] + `(${preSelectedAccount['account']})`,
                value: preSelectedAccountId,
                accountNum: parseInt(preSelectedAccount['account'], 10) || 0
            } : parentAccount || (!isParent && validPostingAccount && !selectedAccountIds.has(preSelectedAccountId!) ? {
                label: preSelectedAccount['name'] + `(${preSelectedAccount['account']})`,
                value: preSelectedAccountId,
                accountNum: parseInt(preSelectedAccount['account'], 10) || 0
            } : null);

            if (account_id?.value) {
                const selectedHierarchy = hierarchyAccountByIdRef.current[+account_id.value];
                const isReusableContainer = isParent
                    && ['GH', 'SH', 'SSH'].includes(selectedHierarchy?.reportCode)
                    && !!selectedHierarchy?.hasChildren
                    && !(account_id as any).proposedName;
                if (!isReusableContainer) {
                    selectedAccountIds.add(+account_id.value);
                }
            }
            const isBank = normalizeQBOAccountType(record['AccountType']) === 'bank';
            // Being in the bank section of the chart and being reconcilable are different
            // things. QBO types cash-on-hand and petty cash as AccountType "Bank", and it
            // keeps deleted accounts (it only renames them "(deleted)" — it cannot remove
            // one that has history). Both belong in CASH AND CASH EQUIVALENTS(10020-11990)
            // and keep their balances; neither has a statement or a feed to reconcile
            // against, so neither should arrive pre-ticked for bank registration. The user
            // can still tick either one by hand.
            const isReconcilableBank = isBank
                && (record['Active'] !== 0)
                && (normalizeQBOAccountType(record['AccountSubType']) !== 'cashonhand');
            const resolvedSource = (isGenerated || isSavedParentContainer)
                ? (record['mapping_source'] || (isSavedParentContainer ? 'parent' : null))
                : parentAccount?.proposedName
                    ? 'first_valid'
                    : (parentAccount ? 'parent' : record['mapping_source'] || null);

            preparedRows.push({
                formIndex: index,
                checked: isReconcilableBank,
                record_id: record['id'],
                name: record['FullyQualifiedName'],
                accountType: record['AccountType'],
                accountSubType: record['AccountSubType'],
                Id_qbo: record['Id_qbo'] || null,
                classification: record['Classification'],
                account_id,
                revrex_bank_name: preSelectedAccount?.revrex_bank_name || '',
                revrex_bank_number: preSelectedAccount?.revrex_bank_number || '',
                acc_num: record['AcctNum'],
                mapping_source: resolvedSource,
                parent_description: parentAccount?.proposedName
                    ? (record['FullyQualifiedName'] || record['NAME'] || '')
                    : (preSelectedAccount?.description || record['FullyQualifiedName'] || record['NAME'] || ''),
                isParent,
                isChild: !!record['ParentRef_value'],
                parentQboId: record['ParentRef_value'] || null,
                depth: (record['FullyQualifiedName'] || '').split(':').length - 1
            });
        });

        formReplace(preparedRows);
        setAutoMappingsReviewed(false);
    }, [records, parentAccountsLoaded, classificationMapLoaded])
    /*useEffect(() => {
        if (fields.length == 0) {
            if (checkAllRef.current) {
                checkAllRef.current.checked = false;
            }
        }
    }, [fields])*/


    // onLoaded runs only after the response lands, so callers can order work that must
    // see the GL accounts this request may have generated (see the mount effect above).
    // It also runs on failure: the chart is still worth loading, and leaving the screen
    // with no options at all would be worse than loading a chart that may be stale.
    const fetchCAList = (onLoaded?: () => void) => {
        if (!branchData?.id) return;

        const params: any = {
            "page": 1,
            "per_page": 500,
            branch_id: +branchData['id']
        }

        setIsLoading(true);
        fetchQuickBookAccountCharts(params, (message: string, resp: any) => {
            reset();
            setRecords(sortQBOAccounts(resp.data.data.result));
            setIsLoading(false);
            onLoaded?.();
        }, (message: string, resp: any) => {
            setIsLoading(false);
            toast.error(message);
            onLoaded?.();
        })
    }

    const getSelectedAccountIds = (currentIndex: number) => {
        const selectedIds = new Set<number>();

        (getValues('coa') || []).forEach((item, index) => {
            const selectedValue = item?.account_id?.value;
            if ((index !== currentIndex) && selectedValue) {
                if (isReusableContainerSelection(item)) return;
                selectedIds.add(+selectedValue);
            }
        });

        return selectedIds;
    }

    const fetchAllGlAccounts = () => {
        if (!branchData?.id) return;

        const params: any = {
            page: 1,
            per_page: 5000,
            branch_id: +branchData['id']
        };
        glAccountsList(params, (message: string, resp: any) => {
            glAccountsCacheRef.current = resp.data.data.result.map((e: any) => ({
                label: e['name'] + `(${e['account']})`,
                value: +e['id'],
                accountNum: parseInt(e['account'], 10) || 0
            }));
            setGlAccountsLoaded(true);
        }, (message: string, resp: any) => {
            toast.error(message);
        });

        getListTree({ branch_id: +branchData['id'] }, (message: string, resp: any) => {
            hierarchyAccountsRef.current = flattenAccounts(resp?.data?.data || []);
            hierarchyAccountByIdRef.current = hierarchyAccountsRef.current.reduce((acc: Record<number, HierarchyAccount>, account) => {
                acc[account.value] = account;
                return acc;
            }, {});
            setParentAccountsLoaded(true);
        }, (message: string, resp: any) => {
            toast.error(message);
            setParentAccountsLoaded(true);
        });

        // Single source for AccountType/Classification -> RevRex section anchors (see
        // get_qbo_classification_map on the backend) — replaces the separately-maintained
        // QB_ACCOUNT_TYPE_PARENT_ACCOUNTS/QB_CLASSIFICATION tables that had drifted from it.
        fetchQboClassificationMap({}, (message: string, resp: any) => {
            qboClassificationMapRef.current = resp?.data?.data || { by_account_type: {}, by_account_sub_type: {}, by_classification: {} };
            setClassificationMapLoaded(true);
        }, (message: string, resp: any) => {
            toast.error(message);
            setClassificationMapLoaded(true);
        });
    };

    const fechAccountList = (
        searchText: string = '',
        classification: ClassificationType = 'Liability',
        accountType: string = '',
        currentIndex: number = -1,
        currentValue: number | string | null = null,
        page: number = 1,
        accountName: string = '',
        rowsOverride?: any[],
        accountSubType: string = ''
    ): Promise<{ options: any[]; hasMore: boolean }> => {
        const selectedAccountIds = getSelectedAccountIds(currentIndex);
        const parentAccounts = getParentAccountsForRow(classification, accountType, accountName, accountSubType);
        const currentRow = currentIndex >= 0 ? (rowsOverride?.[currentIndex] || getValues(`coa.${currentIndex}`)) : null;
        const mappedParentId = getMappedQboParentId(currentRow, rowsOverride || undefined);

        if (hierarchyAccountsRef.current.length > 0) {
            const search = searchText.toUpperCase();
            const scopeRootIds = hierarchyAccountsRef.current
                .filter(account => parentAccounts.includes(account.accountNum))
                .map(account => account.value);
            const statement = ['Asset', 'Liability', 'Equity'].includes(classification) ? 'BS' : 'PL';
            const options = hierarchyAccountsRef.current
                .filter(account => POSTING_REPORT_CODES.has(account.reportCode))
                .filter(account => {
                    if (mappedParentId) return account.ancestorIds.includes(+mappedParentId);
                    if (scopeRootIds.length > 0) {
                        return scopeRootIds.includes(account.value)
                            || scopeRootIds.some(rootId => account.ancestorIds.includes(rootId));
                    }
                    return account.financialStatement === statement;
                })
                .filter(account => !search || account.label.toUpperCase().includes(search) || String(account.accountNum).startsWith(search))
                .filter(account => (currentValue && (+currentValue === account.value)) || !selectedAccountIds.has(account.value))
                // Same rule as getParentOptions: ascending by account number only, no
                // hardcoded preference reordering. Without this, options come back in
                // whatever order hierarchyAccountsRef.current happens to hold them (original
                // seed order, plus anything created later always appended at the end) —
                // so "first available" wasn't actually the lowest account number.
                .sort((a, b) => a.accountNum - b.accountNum);
            return Promise.resolve({ options, hasMore: false });
        }

        // For unfiltered types (OtherIncome/OtherExpense) use client-side cache
        // For filtered types use API call (small result sets, needs parent_id resolution)
        if ((parentAccounts.length === 0) && (glAccountsCacheRef.current.length > 0)) {
            let filtered = glAccountsCacheRef.current;

            if (searchText) {
                const search = searchText.toUpperCase();
                filtered = filtered.filter(a =>
                    a.label.toUpperCase().includes(search) ||
                    String(a.accountNum).startsWith(search)
                );
            }

            filtered = filtered.filter(a => {
                if (currentValue && (+currentValue === a.value)) return true;
                return !selectedAccountIds.has(a.value);
            });

            const options = [...filtered]
                .sort((a, b) => (a.accountNum || 0) - (b.accountNum || 0))
                .map(a => ({ label: a.label, value: a.value }));
            return Promise.resolve({ options, hasMore: false });
        }

        // Filtered types — use API with parent_account_in
        return new Promise<any>((resolve, reject) => {
            const params: any = {
                page: 1,
                per_page: 1000,
                branch_id: +branchData['id']
            };
            if (searchText) {
                params['name'] = searchText;
            }
            if (parentAccounts.length > 0) {
                params['parent_account_in'] = parentAccounts.join(',');
            }
            glAccountsList(params, (message: string, resp: any) => {
                const data = resp.data.data.result.map((e: any) => ({
                    label: e['name'] + `(${e['account']})`,
                    value: e['id'],
                    accountNum: parseInt(e['account'], 10) || 0
                })).filter((option: any) => {
                    if (currentValue && (+currentValue === +option.value)) return true;
                    return !selectedAccountIds.has(+option.value);
                }).sort((a: any, b: any) => a.accountNum - b.accountNum);
                return resolve({ options: data, hasMore: false });
            }, (message: string, resp: any) => {
                toast.error(message);
                return resolve({ options: [], hasMore: false });
            });
        });
    }

    // Find an existing RevRex account (header to reuse, or leaf to promote) for a
    // QBO parent by name, using the backend's real matcher (exact -> normalised ->
    // fuzzy/token-overlap) instead of this screen's own exact-equality-only
    // `exactMatches` in getParentOptions. Reuse/promote before ever creating.
    const matchExistingAccount = (row: any, parentAccountId?: number | null, childCount: number = 0): Promise<{ account_id: number; report_code: string; needs_promotion: boolean; is_container?: boolean } | null> => {
        const name = row.name || row.FullyQualifiedName || row.NAME || '';
        if (!name) return Promise.resolve(null);
        const params: any = {
            // Lets the backend reject a leaf that could never be promoted because it
            // cannot own the numeric span these children need — see has_clean_child_span.
            child_count: childCount,
            branch_id: +branchData['id'],
            account_name: name,
            account_type: row.accountType || row.AccountType || '',
            // Without the subtype the backend's classify_to_grand_header can't
            // tell e.g. Expense/InterestPaid from Expense/Advertising, and would
            // search SG&A instead of INTEREST for an "Interest Expense" parent.
            account_sub_type: row.accountSubType || row.AccountSubType || '',
            classification: row.classification || row.Classification || '',
        };
        // For a child, narrow candidates to the mapped parent's own posting children.
        if (parentAccountId) params.parent_account_id = parentAccountId;
        return new Promise((resolve) => {
            matchQboParent(params, (message: string, resp: any) => {
                resolve(resp?.data?.data || null);
            }, () => resolve(null));
        });
    };

    const fetchAvailableAccountNumber = (row: any, rowsOverride?: any[]): Promise<any | null> => {
        const parentAccounts = getParentAccountsForRow(row.classification as ClassificationType, row.accountType, row.name || '');
        const createRange = getCreateRangeForRow(row);
        const mappedParent = hierarchyAccountByIdRef.current[+getMappedQboParentId(row, rowsOverride)];
        const params: any = {
            branch_id: +branchData['id'],
            available_account: false,
        };
        // A mapped parent's own range is the most specific scope available — a child
        // must be numbered inside its actual parent, not just somewhere in the parent's
        // broader QBO-type section (that's how a child previously landed on an unrelated
        // number while still recording the right parent_id). Falls back to the type-based
        // scoping only when there's no mapped parent to confine to.
        if (hasUsableChildRange(mappedParent)) {
            params.start_range = mappedParent!.startRange;
            params.end_range = mappedParent!.endRange;
        } else if (mappedParent?.accountNum) {
            // Parent is either a posting leaf awaiting promotion or a new/self-range
            // header. Until the backend gives it a real child range, use the small
            // promoted-parent window directly above it (e.g. 8002 -> 8003..8012).
            params.start_range = mappedParent.accountNum + 1;
            params.end_range = mappedParent.accountNum + PROMOTION_CHILD_RANGE_SPAN;
        } else if (createRange) {
            params.start_range = createRange.start_range;
            params.end_range = createRange.end_range;
        } else if (parentAccounts.length > 0) {
            params.parent_account_in = parentAccounts.join(',');
        }

        // A new header must land somewhere its children can actually follow it. The
        // slot was previously chosen as "first free number", ignoring how many children
        // were coming: PROFESSIONAL FEES was created at 8002 with only 8003-8005 free
        // before 8006, so a third child had nowhere to go and the row was left blank
        // for review with no way to resolve it. Require the whole family's span --
        // childCount children plus the total row -- to be free before taking a slot.
        const requiredSpan = row?.isChild ? 0 : countQboChildRows(row, rowsOverride);
        const takenNumbers = new Set(hierarchyAccountsRef.current.map(a => a.accountNum));
        const slotFitsFamily = (candidate: number) => {
            for (let n = candidate + 1; n <= (candidate + requiredSpan + 1); n += 1) {
                if (takenNumbers.has(n)) return false;
            }
            return true;
        };

        return new Promise((resolve) => {
            chartAvailableAccounts(params, (message: string, resp: any) => {
                const groups = (resp?.data?.data || []).filter((group: any) => group?.available?.length > 0);
                let firstGroup: any = null;
                let firstAccount: any = null;
                for (const group of groups) {
                    const fit = requiredSpan > 0
                        ? group.available.find((n: any) => slotFitsFamily(+n))
                        : group.available[0];
                    if (fit !== undefined) {
                        firstGroup = group;
                        firstAccount = fit;
                        break;
                    }
                }
                if (!firstAccount) return resolve(null);
                // A QBO child MUST be parented to its own mapped parent. Falling back to
                // the section id here silently reparented it one level up: "Computers"
                // was created under FIXED ASSETS(2001) instead of FURNITURE AND
                // EQUIPMENT(2003) while its sibling "Dental Chair" landed correctly —
                // the parent_id-vs-range divergence this whole flow exists to prevent,
                // and it surfaces later as a section-mismatch warning on a row the user
                // never touched. Better to create nothing and leave the row blank.
                if (row?.isChild && !mappedParent?.value) return resolve(null);
                resolve({
                    account: firstAccount,
                    parent_id: mappedParent?.value || firstGroup.section_id,
                });
            }, () => resolve(null));
        });
    };

    const fetchAccountDefaults = (account: number): Promise<any | null> => {
        return new Promise((resolve) => {
            getAccountLebel({ account, branch_id: +branchData['id'] }, (message: string, resp: any) => {
                resolve(resp?.data?.data || null);
            }, () => resolve(null));
        });
    };

    const createAutoGlAccount = async (row: any, isParent: boolean = false, rowsOverride?: any[]): Promise<any | null> => {
        const available = await fetchAvailableAccountNumber(row, rowsOverride);
        if (!available) return null;

        const defaults = await fetchAccountDefaults(available.account);
        if (!defaults?.report_code || !defaults?.account_type) return null;
        const accountChart = defaults?.account_chart || {};
        const params = {
            account: available.account,
            parent_id: available.parent_id,
            report_code: isParent ? 'SSH' : (accountChart.report_code || 'VP'),
            parenthisys_control: defaults?.account_type || accountChart.parenthisys_control || 'D',
            financial_statement: ['Asset', 'Liability', 'Equity'].includes(row.classification) ? 'BS' : 'PL',
            name: row.name,
            description: row.name,
            branch_id: +branchData['id'],
            // Persisted origin marker: without it a generated account is
            // indistinguishable from a template one after reload, because it carries
            // the QBO name and so exact-matches on every later pass.
            is_third_party_created: 1,
        };

        return new Promise((resolve) => {
            createChart(params, (message: string, resp: any) => {
                const created = resp?.data?.data;
                resolve(created?.id ? created : null);
            }, () => resolve(null));
        });
    };

    const registerCreatedAccount = (created: any, row: any, isParent: boolean = false) => {
        const parent = hierarchyAccountByIdRef.current[+created.parent_id];
        const shortLabel = `${created.name}(${created.account})`;
        const option = {
            label: parent ? `${parent.label} > ${shortLabel}` : shortLabel,
            shortLabel,
            value: +created.id,
            accountNum: parseInt(created.account, 10) || 0,
            parentId: created.parent_id ? +created.parent_id : null,
            reportCode: created.report_code || (isParent ? 'SSH' : 'VP'),
            financialStatement: created.financial_statement || '',
            ancestorIds: parent ? [...parent.ancestorIds, parent.value] : [],
            name: created.name || row.name,
            hasChildren: false,
            isNewAccount: true,
            isThirdPartyCreated: !!created.is_third_party_created,
            // Backend sets a real range on a newly-created header (see
            // create_account_chart's header_total_row handling) — without copying
            // it here, every downstream range check (hasUsableChildRange,
            // getCreateSectionForRow, fetchAvailableAccountNumber) stays blind to
            // it for the rest of the session, since getListTree is only ever
            // fetched once on mount and never refreshed after a save.
            startRange: created.start_range || undefined,
            endRange: created.end_range || undefined,
        };
        hierarchyAccountsRef.current = [...hierarchyAccountsRef.current, option];
        hierarchyAccountByIdRef.current[+created.id] = option;
        if (parent) parent.hasChildren = true;
        glAccountsCacheRef.current = [...glAccountsCacheRef.current, option];
        return option;
    };

    const filterAccountList = async (
        searchText: string,
        prevOptions: any[],
        additional: { page: number },
        classification: ClassificationType,
        accountType: string,
        currentIndex: number,
        currentValue: number | string | null,
        accountName: string = ''
    ) => {
        const { options, hasMore } = await fechAccountList(searchText, classification, accountType, currentIndex, currentValue, additional.page, accountName);
        return {
            options,
            hasMore,
            additional: { page: additional.page + 1 }
        };
    };

    const filterParentAccountList = async (searchText: string, currentIndex: number, currentValue: number | null) => {
        const row = getValues(`coa.${currentIndex}`);
        const selectedAccountIds = getSelectedAccountIds(currentIndex);
        const search = searchText.toUpperCase();
        const options = getParentOptions(row)
            .filter(account => !search || account.label.toUpperCase().includes(search) || String(account.accountNum).startsWith(search))
            .filter(account => (currentValue === account.value) || !selectedAccountIds.has(account.value));
        return { options, hasMore: false, additional: { page: 1 } };
    };

    const handleParentAccountChange = async (index: number, selection: any, onChange: (value: any) => void) => {
        onChange(selection);
        setValue(`coa.${index}.mapping_source`, 'manual', { shouldDirty: true });

        const parentQboId = String(getValues(`coa.${index}.Id_qbo`) || '');
        const recordsById = new Map<string, any>(records.map((record: any) => [String(record.Id_qbo), record]));
        const isQboDescendant = (row: any) => {
            let parentId = row.parentQboId;
            const seen = new Set<string>();
            while (parentId && !seen.has(String(parentId))) {
                if (String(parentId) === parentQboId) return true;
                seen.add(String(parentId));
                parentId = recordsById.get(String(parentId))?.ParentRef_value;
            }
            return false;
        };

        (getValues('coa') || []).forEach((row: any, rowIndex: number) => {
            if (!isQboDescendant(row) || !row.account_id?.value) return;
            const target = hierarchyAccountByIdRef.current[+row.account_id.value];
            if (!selection || !target?.ancestorIds.includes(+selection.value)) {
                setValue(`coa.${rowIndex}.account_id`, null, { shouldDirty: true });
                setValue(`coa.${rowIndex}.mapping_source`, null, { shouldDirty: true });
            }
        });
        setAccountListVersion(version => version + 1);
    };

    useEffect(() => {
        if (!glAccountsLoaded || !classificationMapLoaded || (fields.length === 0)) {
            setIsAutoFilling(false);
            return;
        }

        const rows = getValues('coa') || [];
        const workingRows = rows.map((row: any) => ({ ...row }));
        const missingRows = rows
            .map((row: any, index: number) => ({ row, index }))
            .filter(({ row }: any) => !row?.account_id?.value)
            // Three tiers: parents, then their children, then everything flat.
            //
            // Parents before children because a child's mappedParentId
            // (getMappedQboParentId) only exists once its parent's account_id is set in
            // workingRows; processing a child first left it unscoped and it could land on
            // any unused posting account in the section rather than under its real parent.
            //
            // Children before FLAT rows because a new header reserves a numeric span for
            // children that do not exist yet. While that span sits empty, an unrelated
            // flat row allocating from the same section takes a number inside it and — as
            // containment is by number — gets parented to that header. Observed live:
            // TRAVEL(8002, span 8002-8005) had BUSINESS TAX(8003) and DONATIONS(8004)
            // land inside it, leaving its real child "Transportation" with nowhere to go
            // and its dropdown empty. Completing each family first means the family's
            // numbers are already taken by the time flat rows allocate, which closes the
            // window without restricting where flat rows may go — filtering the allocator
            // by header containment was measured and would have cut flat income slots
            // from 915 to 7, since one section spans that whole grand header.
            //
            // Array.prototype.sort is stable, so order within each tier is preserved.
            .sort((a: any, b: any) => {
                const tier = (row: any) => (row?.isParent ? 0 : row?.isChild ? 1 : 2);
                return tier(a.row) - tier(b.row);
            });
        if (missingRows.length === 0) {
            setIsAutoFilling(false);
            return;
        }

        const key = `${accountListVersion}:` + missingRows.map(({ row, index }: any) => `${index}:${row?.Id_qbo || row?.name || ''}`).join('|');
        if (autoFillKeyRef.current === key) return;
        autoFillKeyRef.current = key;

        let cancelled = false;
        setIsAutoFilling(true);
        (async () => {
            try {
                const usedIds = new Set<number>(
                    rows
                        .map((row: any) => row?.account_id?.value)
                        .filter(Boolean)
                        .map((value: any) => +value)
                );
                let changed = false;
                let createdAny = false;

                for (const { row, index } of missingRows) {
                    const workingRow = workingRows[index] || row;

                    // Deterministic ordering: never fill a child before its parent has
                    // resolved. Parents are sorted first in this same pass, so by the time
                    // a child is reached its parent's account_id is already in workingRows
                    // — this only skips children whose parent genuinely couldn't be
                    // resolved. Those are picked up on the re-run triggered when the parent
                    // is set (handleParentAccountChange bumps accountListVersion).
                    if (isChildWithUnresolvedParent(workingRow, workingRows)) {
                        continue;
                    }

                    // For a QBO parent, try the backend's real matcher (fuzzy/token
                    // scoring) before this screen's own exact-equality-only
                    // getParentOptions — it can find e.g. a real INSURANCE header
                    // that "Insurance Expense" would otherwise miss and create a
                    // duplicate for instead.
                    // Every automatic mapping must be a NAME match — never "the first
                    // unused slot in scope". That positional pick is what produced
                    // confident-looking wrong mappings (QBO "Auto Insurance" ->
                    // GENERAL(8130) because it was simply the lowest free slot under
                    // INSURANCE). Both parents and children now go through the backend's
                    // matcher (_try_match: exact -> normalised -> hierarchical-suffix ->
                    // fuzzy/token), children scoped to their mapped parent's own children.
                    const mappedParentId = workingRow.isParent
                        ? null
                        : getMappedQboParentId(workingRow, workingRows);
                    const match = await matchExistingAccount(
                        workingRow,
                        mappedParentId ? +mappedParentId : null,
                        workingRow.isParent ? countQboChildRows(workingRow, workingRows) : 0,
                    );
                    if (cancelled) return;

                    let option: any = null;
                    if (match && (match.is_container || !usedIds.has(+match.account_id))) {
                        const matchedOption = hierarchyAccountByIdRef.current[+match.account_id] || null;
                        option = matchedOption ? {
                            ...matchedOption,
                            isContainer: !!match.is_container,
                            proposedName: match.needs_promotion ? (workingRow.name || workingRow.NAME || workingRow.FullyQualifiedName || '') : matchedOption.proposedName,
                        } : null;
                    }

                    // Parents only: fall back to reusing an unused structural slot, which
                    // getParentOptions restricts to exact-name matches (anything else it
                    // returns carries proposedName and is filtered out here). Still a name
                    // match, not a positional one.
                    if (!option && workingRow.isParent) {
                        option = getParentOptions(workingRow, workingRows)
                            .filter((o: any) => !o.proposedName)
                            .find((o: any) => !usedIds.has(+o.value)) || null;
                    }

                    // No name match anywhere: create a correctly-named account inside the
                    // right parent/section. That's a true mapping rather than a guess —
                    // and if there's no room to create, the row is left blank for the user
                    // (client decision: blank over confidently wrong).
                    let wasCreated = false;
                    if (!option) {
                        const created = await createAutoGlAccount(workingRow, !!workingRow.isParent, workingRows);
                        if (!created?.id) continue;
                        option = registerCreatedAccount(created, workingRow, !!workingRow.isParent);
                        createdAny = true;
                        wasCreated = true;
                    }

                    if (!isReusableContainerSelection({ ...workingRow, account_id: option })) {
                        usedIds.add(+option.value);
                    }
                    // Keep description in sync with whichever account this effect just
                    // resolved to (reused or newly created) — without this, a row that
                    // previously had a different mapping keeps submitting its OLD, stale
                    // description even though account_id/name now point at a fresh account.
                    const resolvedDescription = workingRow.name || '';
                    // Record what actually happened, not a single catch-all. This used to
                    // stamp 'first_valid' on every row it touched, which drives the amber
                    // "an arbitrary free slot was reused" highlight — so a newly CREATED
                    // account (named from the QBO account, correct by construction) was
                    // flagged with a warning meant for low-confidence reuse. It also made
                    // the colour depend on timing rather than provenance: a created child
                    // is re-resolved on every load (its saved mapping is rejected until its
                    // parent resolves) so it turned amber, while an identically-created flat
                    // VP kept its persisted 'matched' and stayed green.
                    const resolvedSource = wasCreated ? 'created' : 'matched';
                    setValue(`coa.${index}.account_id`, option, { shouldDirty: true });
                    setValue(`coa.${index}.mapping_source`, resolvedSource, { shouldDirty: true });
                    setValue(`coa.${index}.parent_description`, resolvedDescription, { shouldDirty: true });
                    workingRows[index] = { ...workingRow, account_id: option, mapping_source: resolvedSource, parent_description: resolvedDescription };
                    clearErrors(`coa.${index}.account_id`);
                    changed = true;
                }

                if (changed) {
                    setAutoMappingsReviewed(false);
                }
                if (createdAny) {
                    setAccountListVersion(v => v + 1);
                }
            } finally {
                if (!cancelled) setIsAutoFilling(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [glAccountsLoaded, classificationMapLoaded, fields.length, accountListVersion]);



    const handleCheckAllChange = (e: any) => {
        if (checkAllRef.current) {
            checkAllRef.current.checked = e.target.checked;

            fields.forEach((record: any, index: number) => {
                setValue(`coa.${index}.checked`, e.target.checked)
            })

            /*records.map((record: any, index: number) => {
                setValue(`banks.${index}.checked`, e.target.checked)
            })*/
        }
        // setChecked(
        //     e.target.checked ? records.map((c: any) => c.id) : []
        // );
    };

    const handleCheckAll = (e: any, index: number) => {
        setValue(`coa.${index}.checked`, e.target.checked);
        if (e.target.checked) {
            const checkedRecords = fields.filter((record: any, index: number) => getValues(`coa.${index}.checked`) === true);

            if (checkedRecords.length == fields.length) {
                if (checkAllRef.current) {
                    checkAllRef.current.checked = true;
                }
            }
        } else {
            const uncheckedRecords = fields.filter((record: any, index: number) => getValues(`coa.${index}.checked`) === false);

            if (uncheckedRecords.length) {
                if (checkAllRef.current) {
                    checkAllRef.current.checked = false;
                }
            }
        }
    }

    const processData = (data: any) => {
        const coaList = data['coa'] || [];
        let firstPendingIndex = -1;

        for (let index = 0; index < coaList.length; index++) {
            const item = coaList[index];
            const liveRow = { ...(fields[index] as any), ...(item || {}) };
            if (isPendingRowAction(liveRow, index, coaList)) {
                firstPendingIndex = index;
                break;
            }
        }

        if (firstPendingIndex !== -1) {
            toast.error(t("please.save.all.pending.actions") || "Please save or map all pending account actions before submitting.");
            scrollToInvalidDropdown(firstPendingIndex);
            return;
        }

        const hasError = _handleDuplicateValidation();
        if (hasError) {
            toast.error(t("duplicate.error"));
            const firstErrorIndex = coaList.findIndex((item: any, index: number) => errors?.coa?.[index]);
            if (firstErrorIndex !== -1) {
                scrollToInvalidDropdown(firstErrorIndex);
            }
            return;
        }
        //console.log(data);
        const toRemoveItems: number[] = [];

        // console.log('COA data', data['coa']);

        const records = (data['coa'] || [])
            .map((d: any, index: number) => {
                if (!d?.account_id?.value || isNaN(+d.account_id.value)) {
                    return null;
                }
                toRemoveItems.push(index);
                return {
                    account_id: +d.account_id.value,
                    acc_num: d.acc_num,
                    Id_qbo: d.Id_qbo,
                    is_parent: !!d.isParent,
                    description: d.parent_description || d.name,
                    name: d.name
                };
            })
            .filter((e: any) => e); // Filter out null values
        const bankRows = data['coa']
            // `d.checked` is the row's own tick box, and it is what decides whether a QBO
            // bank account is registered for reconciliation. Filtering on account type
            // alone sent every mapped bank row to the registration modal, so unticking one
            // did nothing — it still appeared, and still had to be filled in.
            .filter((d: any) => (normalizeQBOAccountType(d.accountType) === 'bank') && d.account_id?.value && d.checked)
            .map((d: any) => {
                const glId = +d.account_id.value;
                const gl_account_number: number | null = d.account_id?.accountNum ?? null;
                console.log('[QBCOA] bankRow built:', { qbo_name: d.name, label: d.account_id.label, gl_account_number, gl_account_id: glId });
                return {
                    gl_account_id: glId,
                    gl_account_number,
                    gl_account_label: d.account_id.label,
                    qbo_name: d.name,
                    revrex_bank_name: d.revrex_bank_name || '',
                    revrex_bank_number: d.revrex_bank_number || ''
                };
            });

        const hasMappedBankRows = data['coa']
            .some((d: any) => (normalizeQBOAccountType(d.accountType) === 'bank') && d.account_id?.value);

        if (hasMappedBankRows && (bankRows.length === 0)) {
            toast.error(t('toast.selectError'));
            setBankSelectionError(true);
            return;
        }
        setBankSelectionError(false);

        if (bankRows.length > 0) {
            if (savedBankDetails.length > 0) {
                // Details captured on an earlier attempt, so re-check them against the
                // ticks as they stand now: a bank unticked after the modal was filled in
                // must not still be registered on the retry.
                const stillSelected = savedBankDetails.filter((saved: any) =>
                    bankRows.some((row: any) => row.gl_account_id === saved.gl_account_id)
                );
                postData(records, toRemoveItems, stillSelected);
                return;
            }
            setPendingBankRows(bankRows);
            setPendingSubmitData({ records, toRemoveItems });
            setShowBankRegModal(true);
            return;
        }
        postData(records, toRemoveItems);
    }

    const handleBankRegConfirm = (bankDetails: any[]) => {
        setShowBankRegModal(false);
        const submit = pendingSubmitData;
        setPendingSubmitData(null);
        setPendingBankRows([]);
        setSavedBankDetails(bankDetails);
        if (!submit) return;
        postData(submit.records, submit.toRemoveItems, bankDetails);
    }

    const handleBankRegCancel = () => {
        setShowBankRegModal(false);
        setPendingSubmitData(null);
        setPendingBankRows([]);
    }
    const postData = (records: any, toRemoveItems: number[], bankDetails: any[] = []) => {
        if (!records.length) {
            toast.error(t("no.record.selected"));
            return;
        }

        setIsLoading(true);
        // Preview first: any QBO parent mapped to a plain posting account promotes it
        // to a header (new range, re-pointed children, a new total row) — a real
        // structural change to the tenant's chart. Surface that before committing to it.
        postQuickBookAccountCharts({ records, branch_id: +branchData['id'], dry_run: true }, (message: string, resp: any) => {
            const promotions = resp?.data?.data?.promotions || [];
            // This callback runs synchronously INSIDE the POST_ACCOUNT_CHARTS saga's own
            // generator. Dispatching that same action again from here re-enters it and
            // redux-saga throws "already executing generator" — which killed the root
            // task ("Tasks cancelled due to error"), so no commit request was ever sent,
            // isLoading was never cleared, and every other watcher in the app died with
            // it. So the commit must happen outside this frame.
            //
            // Hand off through state rather than a timer. A setTimeout here is dropped
            // if anything re-renders or remounts before it fires, and then the commit
            // POST is simply never issued -- no error, no request, the mapping silently
            // not saved. Parking the payload in state instead means confirm+commit run
            // from React's own effect cycle: still outside the saga frame, but it
            // cannot be discarded.
            console.log('[QBCOA submit] 1/5 dry-run OK, parking payload', { promotions: promotions.length, records: records.length });
            // Release the page loader while the confirm modal is up: postData set it
            // before the dry run, and leaving it on renders the full-screen spinner
            // OVER the modal, swallowing the Continue click.
            if (promotions.length > 0) setIsLoading(false);
            setPendingPromotionConfirm({ promotions, records, toRemoveItems, bankDetails });
        }, (message: string, resp: any) => {
            setIsLoading(false);
            toast.error(message);
        });
    }

    const submitAccountChartData = (records: any, toRemoveItems: number[], bankDetails: any[] = []) => {
        console.log('[QBCOA submit] 4/5 dispatching COMMIT', { records: records?.length, banks: bankDetails?.length });
        if (records.length) {
            postQuickBookAccountCharts({ records, branch_id: +branchData['id'] }, async (message: string, resp: any) => {
                console.log('[QBCOA submit] 5/5 COMMIT SUCCESS', message);
                if (bankDetails.length > 0) {
                    await registerRevrexBanks(bankDetails);
                }
                setIsLoading(false);
                setSavedBankDetails([]);
                toast.success(message)
                formRemove(toRemoveItems)
                if (records.length == fields.length) {
                    // QBD's transaction import (Checks/Deposits/Transfers/...) is pushed by
                    // the desktop connector in a run separate from -- and not synchronized
                    // with -- gl-api's automatic post-beginning-balance cascade, so the
                    // cascade can build a month's balance sheet snapshot before that month's
                    // transactions have all landed (see QBO_vs_QBD_Import_Architecture.docx
                    // and gl-api's qbo_desktop_import/controller.py's _seed_qbd_report_logs).
                    // Completing COA mapping is the practical signal that the user is done
                    // reviewing an import that's had time to fully land, so re-run the
                    // cascade from the branch's earliest reportable month (year=2000 is
                    // gl-api's regenerate endpoint's floor -- see balance_sheet/router.py --
                    // and regenerate_bs_cascade only ever walks it back further to an
                    // earlier detected imbalance, never forward, so this always covers the
                    // whole branch) to clean up anything the race left stale. QBO's import
                    // has no equivalent race (its cascade isn't triggered off a beginning-
                    // balance step split from the transaction import the way QBD's is), so
                    // this is intentionally gated to QBD only -- see gl-api's is_qbd
                    // parameter pattern for the same reasoning applied elsewhere this cycle.
                    if (_isQbdImport) {
                        regenerateBalanceSheet(
                            { year: 2000, month: 1, branch_id: +branchData['id'] },
                            (regenMessage: string, regenResp: any) => {
                                console.log('[QBCOA submit] post-mapping QBD balance sheet regenerate queued', regenResp?.data?.data?.status);
                            },
                            (regenMessage: string, regenResp: any) => {
                                // Non-fatal -- the mapping itself already succeeded and was
                                // reported above; a failed regenerate trigger here just means
                                // the user (or the existing "Regenerate" action on the
                                // Balance Sheet page) needs to retry it manually.
                                console.error('[QBCOA submit] post-mapping QBD balance sheet regenerate failed to queue', regenMessage);
                            }
                        );
                        // QBD only -- ask the user which month QuickBooks Desktop shows
                        // reconciliation complete through, and backfill bank reconciliation
                        // to match (see QBDReconciliationCutoffModal for why this is a user
                        // attestation rather than read back from QuickBooks). The modal's
                        // onDone handles navigate(-1) once it's dismissed either way, so QBD
                        // no longer falls through to the unconditional navigate(-1) below.
                        setShowReconciliationCutoffModal(true);
                        return;
                    }
                    navigate(-1);
                }
                // fetchCAList();
            }, (message: string, resp: any) => {
                console.error('[QBCOA submit] COMMIT FAILED', message, resp);
                setIsLoading(false);
                toast.error(message);
            })
        } else {
            console.warn('[QBCOA submit] aborted: records empty');
            toast.error(t("no.record.selected"));
        }
    }

    // Second half of the submit, split out of the dry-run callback so the commit is
    // dispatched on a clean stack rather than inside that saga's own generator.
    useEffect(() => {
        if (!pendingPromotionConfirm) return;
        const { promotions, records, toRemoveItems, bankDetails } = pendingPromotionConfirm;
        // Nothing structural to approve -> straight through. Anything to approve is
        // rendered as a real modal below; window.confirm was used here before and the
        // browser's "prevent this page from creating additional dialogs" option made it
        // return false without ever showing, silently cancelling every submit.
        if (!promotions.length) {
            setPendingPromotionConfirm(null);
            submitAccountChartData(records, toRemoveItems, bankDetails);
        }
    }, [pendingPromotionConfirm]);

    const handlePromotionConfirm = () => {
        const pending = pendingPromotionConfirm;
        setPendingPromotionConfirm(null);
        if (!pending) return;
        setIsLoading(true);
        submitAccountChartData(pending.records, pending.toRemoveItems, pending.bankDetails);
    };

    const handlePromotionCancel = () => {
        setPendingPromotionConfirm(null);
        setIsLoading(false);
    };

    const registerRevrexBanks = async (bankDetails: any[]): Promise<void> => {
        let failed = 0;
        for (const b of bankDetails) {
            const payload = {
                branch_id: +branchData['id'],
                name: b.network_label,
                desc: b.desc,
                number: '' + b.number,
                gl_account_id: b.gl_account_id,
                gl_account_number: b.gl_account_number,
            };
            await new Promise<void>((resolve) => {
                addBankAccounts(payload,
                    () => resolve(),
                    () => { failed += 1; resolve(); }
                );
            });
        }
        if (failed > 0) toast.error(t('toast.bankFailed', { count: failed }));
    }

    const selectRefs = useRef<SelectInstance<any, false, any>[]>([]);
    const dropdownRowRefs = useRef<(HTMLDivElement | null)[]>([]);
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const coaRequiredMessage = t('quickbook_coa.account_id.required', { ns: 'resources.form-validator' });

    const clearRowAccountError = (index: number) => {
        clearErrors(`coa.${index}.account_id`);
        if (activeInvalidIndex === index) {
            setActiveInvalidIndex(null);
        }
    };

    const scrollToInvalidDropdown = (index: number) => {
        const container = tableContainerRef.current;
        const targetRow = dropdownRowRefs.current[index];

        if (!targetRow) return;

        if (container) {
            const containerRect = container.getBoundingClientRect();
            const rowRect = targetRow.getBoundingClientRect();
            const nextScrollTop = ((container.scrollTop + (rowRect.top - containerRect.top)) - (container.clientHeight / 2)) + (rowRect.height / 2);

            container.scrollTo({
                top: Math.max(nextScrollTop, 0),
                behavior: 'smooth'
            });
        } else {
            targetRow.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        }

        window.setTimeout(() => {
            selectRefs.current[index]?.focus();
        }, 350);
    };

    const getMissingAccountIndexes = (coaRows: any[] = getValues('coa') || []) =>
        coaRows.reduce((indexes: number[], item: any, index: number) => {
            if (!item?.account_id?.value) {
                indexes.push(index);
            }

            return indexes;
        }, []);

    const syncMissingAccountErrors = (coaRows: any[] = getValues('coa') || []) => {
        const missingIndexes = getMissingAccountIndexes(coaRows);

        missingIndexes.forEach((index) => {
            setError(`coa.${index}.account_id`, {
                type: 'manual',
                message: coaRequiredMessage,
            });
        });

        missingValidationIndexes.forEach((index) => {
            if (!missingIndexes.includes(index)) {
                clearErrors(`coa.${index}.account_id`);
            }
        });

        setMissingValidationIndexes(missingIndexes);
        return missingIndexes;
    };

    const advanceToNextInvalidDropdown = (changedIndex: number) => {
        window.setTimeout(() => {
            const updatedRows = getValues('coa') || [];
            const missingIndexes = syncMissingAccountErrors(updatedRows);

            if (missingIndexes.length > 0) {
                const nextInvalidIndex = missingIndexes.includes(changedIndex)
                    ? changedIndex
                    : (missingIndexes.find((index) => index > changedIndex) ?? missingIndexes[0]);

                setActiveInvalidIndex(nextInvalidIndex);
                scrollToInvalidDropdown(nextInvalidIndex);
                return;
            }

            setActiveInvalidIndex(null);
            setValidationActivated(false);
            handleSubmit(processData)();
        }, 0);
    };

    const handleVerifyAndOverwrite = () => {
        if (isAutoFilling) return;

        const coaRows = getValues('coa') || [];
        if ((autoMatchedCount > 0) && !autoMappingsReviewed) {
            toast.error(t('mapping.reviewRequired'));
            return;
        }

        const missingIndexes = syncMissingAccountErrors(coaRows);

        setValidationActivated(true);

        if (missingIndexes.length > 0) {
            setSearchQuery('');
            setActiveFilterTab('all');
            toast.error(t('toast.missingMapping', { count: missingIndexes.length }));
            setActiveInvalidIndex(missingIndexes[0]);
            scrollToInvalidDropdown(missingIndexes[0]);
            return;
        }
        if (activeInvalidIndex !== null) {
            clearErrors(`coa.${activeInvalidIndex}.account_id`);
            setActiveInvalidIndex(null);
        }

        setMissingValidationIndexes([]);
        setValidationActivated(false);
        const formData = getValues();
        processData(formData);
    };

    const handleSelectChange = (index: number, e: any) => {
        setTimeout(() => {
            let nextIndex = index + 1;
            while (nextIndex < selectRefs.current.length) {
                const nextSelect = selectRefs.current[nextIndex];
                if (nextSelect && !nextSelect.props.isDisabled) {
                    nextSelect.focus();
                    break;
                }
                nextIndex++;
            }
        }, 0);
    };

    const handleOpenCreateModal = (rowIndex: number) => {
        const rows: any[] = getValues('coa') || [];
        const row = rows[rowIndex] || fields[rowIndex] || {};
        const parent = getCreateParentForRow(row, rows);
        const section = getCreateSectionForRow(row, parent);
        setParentDetailsRecord(null);
        setParentDetailsAccountOptions([]);
        setCreateModalRowIndex(rowIndex);
        setCreateModalAccountName(row?.name || '');
        setCreateModalSection(section);
        setCreateModalParentAccountIn(section ? null : getParentAccountsForRow(row?.classification as ClassificationType, row?.accountType, row?.name));
        setCreateModalForcedParentId(parent?.value || null);
        setShowCreateModal(true);
    };

    const handleOpenParentDetails = (rowIndex: number) => {
        const row = getValues(`coa.${rowIndex}`);
        const selection = row?.account_id;
        const account = hierarchyAccountByIdRef.current[+selection?.value];
        if (!row || !selection?.value) return;

        const accountOptions = getParentOptions(row).map(option => ({
            label: String(option.accountNum),
            value: option.value,
            accountNum: option.accountNum,
            path: option.label.replaceAll(' > ', ' › '),
            mappingOption: option,
        }));
        const accountOption = accountOptions.find(option => option.value === +selection.value) || {
            label: String(account?.accountNum || selection.accountNum),
            value: +selection.value,
            accountNum: account?.accountNum || selection.accountNum,
            path: (selection.label || account?.label || '').replaceAll(' > ', ' › '),
            mappingOption: selection,
        };

        setCreateModalRowIndex(rowIndex);
        setCreateModalSection(null);
        setCreateModalParentAccountIn(null);
        setCreateModalForcedParentId(null);
        setParentDetailsAccountOptions(accountOptions.length ? accountOptions : [accountOption]);
        setParentDetailsRecord({
            id: selection.value,
            account: accountOption.accountNum,
            accountOption,
            name: row.name,
            description: row.parent_description || row.name,
            path: (selection.label || account?.label || '').replaceAll(' > ', ' › '),
        });
        setShowCreateModal(true);
    };


    const handleCloseCreateModal = (newAccount?: { id?: number, name?: string, account?: string, description?: string, updated?: boolean, metadataOnly?: boolean, accountOption?: any }) => {
        const rowIndex = createModalRowIndex;
        const wasParentDetails = !!parentDetailsRecord;
        setShowCreateModal(false);
        setCreateModalRowIndex(-1);
        setCreateModalSection(null);
        setCreateModalParentAccountIn(null);
        setCreateModalForcedParentId(null);
        setParentDetailsRecord(null);
        setParentDetailsAccountOptions([]);
        if (wasParentDetails) {
            if (newAccount?.metadataOnly && (rowIndex >= 0)) {
                const selection = newAccount.accountOption?.mappingOption;
                if (selection) {
                    handleParentAccountChange(rowIndex, selection, value => {
                        setValue(`coa.${rowIndex}.account_id`, value, { shouldDirty: true });
                    });
                    setValue(`coa.${rowIndex}.mapping_source`, selection.proposedName ? 'first_valid' : 'manual', { shouldDirty: true });
                }
                setValue(`coa.${rowIndex}.parent_description`, newAccount.description || '', { shouldDirty: true });
            }
            return;
        }
        // Refresh the GL accounts cache and bump version to bust AsyncPaginate cache
        fetchAllGlAccounts();
        setAccountListVersion(v => v + 1);
        // Auto-select the newly created account in the dropdown
        if (newAccount?.id && newAccount.name && newAccount.account && (rowIndex >= 0)) {
            clearRowAccountError(rowIndex);
            setValue(`coa.${rowIndex}.account_id`, {
                label: `${newAccount.name}(${newAccount.account})`,
                shortLabel: `${newAccount.name}(${newAccount.account})`,
                value: newAccount.id,
                accountNum: parseInt(newAccount.account, 10) || 0,
                isNewAccount: true,
            });
            setValue(`coa.${rowIndex}.mapping_source`, 'manual');

            if (validationActivated) {
                advanceToNextInvalidDropdown(rowIndex);
            }
        }
    };

    const CreateAccountMenuList = (rowIndex: number) => (props: any) => {
        return (
            <components.MenuList {...props}>
                {props.children}
                <div
                    style={{
                        padding: '8px 12px',
                        borderTop: '1px solid #e0e0e0',
                        cursor: 'pointer',
                        color: '#0d6efd',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        // Pinned to the bottom of the scroll area. This renders after
                        // props.children, so in a section with many candidates (e.g.
                        // Automobile) it sat below the fold and read as "creating an
                        // account isn't offered on this row" — while a row with few
                        // options showed it straight away. Needs an opaque background
                        // so options don't scroll through underneath it.
                        position: 'sticky',
                        bottom: 0,
                        background: '#fff',
                        zIndex: 1,
                    }}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleOpenCreateModal(rowIndex);
                    }}
                >
                    <i className="fa fa-plus-circle" aria-hidden="true"></i>&nbsp;
                    {t('page.createGLAccount')}
                </div>
            </components.MenuList>
        );
    };

    const renderVerifySubmitButton = (isTop: boolean = false) => {
        const isSubmitDisabled = (pendingActionCount > 0) || isLoading || isAutoFilling;

        let tooltipMessage = "";
        if (isAutoFilling) {
            tooltipMessage = t('tooltip.autoMappingInProgress');
        } else if (pendingActionCount > 0) {
            tooltipMessage = t('tooltip.pendingActions', { count: pendingActionCount });
        }

        const buttonElement = (
            <button
                type="button"
                className={`btn qbo-verify-btn ${isSubmitDisabled ? 'disabled' : ''}`}
                onClick={handleVerifyAndOverwrite}
                disabled={isSubmitDisabled}
            >
                {isAutoFilling ? (
                    <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        <span>{t('btn.autoMappingInProgress')}</span>
                    </>
                ) : (
                    <>
                        <i className="fas fa-check-circle me-2" aria-hidden="true"></i>
                        <span>{t("btn.title") || "Verify & Continue"}</span>
                    </>
                )}
            </button>
        );

        if (isSubmitDisabled && tooltipMessage) {
            return (
                <OverlayTrigger
                    placement={isTop ? "bottom" : "top"}
                    overlay={<Tooltip id={`qbo-verify-btn-tooltip-${isTop ? 'top' : 'bottom'}`}>{tooltipMessage}</Tooltip>}
                >
                    <span className="d-inline-block cursor-not-allowed">
                        {buttonElement}
                    </span>
                </OverlayTrigger>
            );
        }

        return buttonElement;
    };

    return (
        <div className="main-container flex-grow-1">
            <div className="container-fluid">
                <div className="page-title pb-3 pt-3" id="ux_page_title">
                    <div className="row align-items-center">
                        <div className="col-sm-6 align-items-center d-flex">
                            <h1 className="h5 font-weight-700 mb-0 d-inline-flex">{t("page.title")}</h1>
                        </div>
                    </div>
                </div>



                {/* --- COMMAND CENTER TOOLBAR & FILTER TABS --- */}
                <div className="qbo-toolbar-card mb-3">
                    <div className="qbo-filter-tabs">
                        <button
                            type="button"
                            className={`qbo-tab-btn qbo-tab-all ${activeFilterTab === 'all' ? 'active' : ''}`}
                            onClick={() => setActiveFilterTab('all')}
                        >
                            {(activeFilterTab === 'all') && <i className="fas fa-check qbo-tab-check" aria-hidden="true"></i>}
                            {t('tabs.all')} <span className="qbo-count-badge">{fields.length}</span>
                        </button>
                        <button
                            type="button"
                            className={`qbo-tab-btn qbo-tab-matched ${activeFilterTab === 'matched' ? 'active' : ''}`}
                            onClick={() => setActiveFilterTab('matched')}
                        >
                            {(activeFilterTab === 'matched') && <i className="fas fa-check qbo-tab-check" aria-hidden="true"></i>}
                            {t('tabs.matched')} <span className="qbo-count-badge qbo-badge-success">{autoMatchedCount}</span>
                        </button>
                        <button
                            type="button"
                            className={`qbo-tab-btn qbo-tab-new ${activeFilterTab === 'new' ? 'active' : ''}`}
                            onClick={() => setActiveFilterTab('new')}
                        >
                            {(activeFilterTab === 'new') && <i className="fas fa-check qbo-tab-check" aria-hidden="true"></i>}
                            {t('tabs.new')} <span className="qbo-count-badge qbo-badge-purple">{autoCreatedCount}</span>
                        </button>
                        <button
                            type="button"
                            className={`qbo-tab-btn qbo-tab-review ${activeFilterTab === 'review' ? 'active' : ''}`}
                            onClick={() => setActiveFilterTab('review')}
                        >
                            {(activeFilterTab === 'review') && <i className="fas fa-check qbo-tab-check" aria-hidden="true"></i>}
                            {t('tabs.review')} <span className="qbo-count-badge qbo-badge-warning">{reviewNeededCount}</span>
                        </button>
                        <button
                            type="button"
                            className={`qbo-tab-btn qbo-tab-pending ${activeFilterTab === 'pending' ? 'active' : ''}`}
                            onClick={() => setActiveFilterTab('pending')}
                        >
                            {(activeFilterTab === 'pending') && <i className="fas fa-check qbo-tab-check" aria-hidden="true"></i>}
                            {t('tabs.pending')} <span className="qbo-count-badge qbo-badge-danger">{pendingActionCount}</span>
                        </button>
                        {isAutoFilling && (
                            <span className="qbo-autofill-pill d-inline-flex align-items-center gap-2 px-3 py-1.5 ms-2" style={{ backgroundColor: '#e0f2fe', color: '#0369a1', borderRadius: '99px', fontSize: '12px', fontWeight: 600, border: '1px solid #bae6fd' }}>
                                <i className="fas fa-spinner fa-spin text-primary"></i> {t('status.autoMapping')}
                            </span>
                        )}
                    </div>

                    <div className="qbo-actions-group">
                        {(fields.length > 0) && renderVerifySubmitButton(true)}
                        <div className="qbo-search-box">
                            <i className="fas fa-search qbo-search-icon"></i>
                            <input
                                type="text"
                                className="form-control qbo-search-input"
                                placeholder={t('search.placeholder')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button type="button" className="qbo-search-clear" onClick={() => setSearchQuery('')}>
                                    <i className="fas fa-times"></i>
                                </button>
                            )}
                        </div>
                        {(autoMatchedCount > 0) && (
                            <button
                                type="button"
                                className={`btn btn-sm ${autoMappingsReviewed ? 'btn-success' : 'btn-primary'} qbo-approve-btn`}
                                onClick={() => setAutoMappingsReviewed(prev => !prev)}
                            >
                                <i className={`fas ${autoMappingsReviewed ? 'fa-check-circle' : 'fa-check'}`}></i> &nbsp;
                                {autoMappingsReviewed ? t('btn.matchedApproved') : t('btn.approveAllMatched', { count: autoMatchedCount })}
                            </button>
                        )}
                    </div>
                </div>

                {isAutoFilling && (
                    <div className="qbo-autofill-banner alert alert-info py-2 px-3 mb-3 d-flex align-items-center justify-content-between shadow-sm" style={{ borderRadius: '10px', border: '1px solid #7dd3fc', backgroundColor: '#f0f9ff' }}>
                        <div className="d-flex align-items-center gap-3">
                            <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: '1.2rem', height: '1.2rem' }}></div>
                            <div>
                                <strong style={{ color: '#0369a1', fontSize: '14px', display: 'block' }}>
                                    {t('banner.title')}
                                </strong>
                                <span style={{ color: '#0284c7', fontSize: '13px' }}>
                                    {t('banner.description')}
                                </span>
                            </div>
                        </div>
                        <span className="badge bg-primary px-3 py-2 text-uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>
                            <i className="fas fa-sync fa-spin me-1"></i> {t('banner.running')}
                        </span>
                    </div>
                )}

                <div className="executive-dashboard">
                    <div className="dashboard-top-part">
                        <div className="filter-btn">
                            <div className={`page-loader ${isLoading || isAutoFilling ? 'visible' : ''}`}>
                                <div className="loader"></div>
                            </div>
                        </div>
                        <Form>
                            <div
                                ref={tableContainerRef}
                                className="table-responsive list-table table-scroll qbo-coa-table-scroll"
                                style={{ maxHeight: contentMaxHeight }}
                            >
                                <table className="table table-default">
                                    <thead ref={theadRef}>
                                        <tr className="qbo-table-header align-middle">
                                            <th className="text-center" style={{ width: 45 }}></th>
                                            <th className="text-start" style={{ width: '25%' }}>{t("table.header.name")}</th>
                                            <th className="text-start" style={{ width: '15%' }}>{t("table.header.account.type")}</th>
                                            <th className="text-start" style={{ width: '15%' }}>{t('table.header.status')}</th>
                                            <th className="text-start" style={{ width: '33%' }}>{t("table.header.gl.account")}</th>
                                            <th className="text-start" style={{ width: '12%' }}>{t('table.header.action')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            let lastRenderedSectionName: string | null = null;
                                            return fields.length > 0 ? (
                                                fields.map((data: any, index: any) => {
                                                    const depth: number = (fields[index] as any).depth || 0;
                                                    const isChild: boolean = !!(fields[index] as any).isChild;
                                                    const previousDepth = ((fields[index - 1] as any)?.depth || 0);
                                                    const nextDepth = ((fields[index + 1] as any)?.depth || 0);
                                                    const isFirstChild = isChild && (!(fields[index - 1] as any)?.isChild || (previousDepth < depth));
                                                    const isLastChild = isChild && (!(fields[index + 1] as any)?.isChild || (nextDepth < depth));
                                                    const hierarchyNodeClass = `qbo-hierarchy-node${isFirstChild ? ' qbo-hierarchy-first' : ''}${isLastChild ? ' qbo-hierarchy-last' : ''}`;
                                                    const isParent: boolean = !!(fields[index] as any).isParent;
                                                    const isBank = normalizeQBOAccountType(fields[index].accountType) === 'bank';
                                                    const sectionName = (fields[index] as any).classification || UNCLASSIFIED_SECTION;
                                                    const sectionSummary = sectionSummaries[sectionName];
                                                    const selectedOption = coaValues?.[index]?.account_id as any;
                                                    const liveRow = { ...(fields[index] as any), ...(coaValues?.[index] || {}) };
                                                    const isFallbackMapping = isFirstValidMapping(liveRow.mapping_source);
                                                    const sectionMismatch = getSectionMismatchWarning(liveRow);
                                                    const selectedHierarchy = hierarchyAccountByIdRef.current[+(selectedOption?.value || 0)];
                                                    const selectedPath = selectedOption?.proposedName ? selectedOption.label : selectedHierarchy?.label;
                                                    const isNewMapping = !!selectedOption?.value && !!(
                                                        isBackendGeneratedMapping(liveRow.mapping_source)
                                                        || selectedOption?.isNewAccount
                                                        || selectedHierarchy?.isNewAccount
                                                        || selectedHierarchy?.isThirdPartyCreated
                                                    );
                                                    const isGeneratedPreview = !!selectedOption?.value && isBackendGeneratedMapping(liveRow.mapping_source);
                                                    const isMatchedMapping = isAutoMapping(liveRow) && !isNewMapping;
                                                    const needsReviewMapping = !selectedOption?.value || isFallbackMapping;
                                                    const isPendingAction = isPendingRowAction(liveRow, index, coaValues || []);

                                                    // Filter by Active Tab
                                                    if ((activeFilterTab === 'matched') && !isMatchedMapping) return null;
                                                    if ((activeFilterTab === 'new') && !isNewMapping) return null;
                                                    if ((activeFilterTab === 'review') && !needsReviewMapping) return null;
                                                    if ((activeFilterTab === 'pending') && !isPendingAction) return null;

                                                    // Filter by Search Query
                                                    if (searchQuery.trim()) {
                                                        const query = searchQuery.toLowerCase().trim();
                                                        const qboName = String(fields[index].name || '').toLowerCase();
                                                        const acctType = String(fields[index].accountType || '').toLowerCase();
                                                        const targetLabel = String(selectedOption?.label || selectedHierarchy?.label || '').toLowerCase();
                                                        const matches = qboName.includes(query) || acctType.includes(query) || targetLabel.includes(query);
                                                        if (!matches) return null;
                                                    }

                                                    const startsSection = sectionName !== lastRenderedSectionName;
                                                    if (startsSection) {
                                                        lastRenderedSectionName = sectionName;
                                                    }

                                                    const hasQboChildren = isParent && !!(fields[index + 1] as any)?.isChild && (((fields[index + 1] as any)?.depth || 0) > depth);
                                                    const parentNeedsChildRange = hasQboChildren && !!selectedOption?.value && !hasUsableChildRange(selectedOption) && !hasUsableChildRange(selectedHierarchy);
                                                    const isPromotedParent = isParent && (isNewOrPromotedParentSelection(selectedOption, selectedHierarchy) || parentNeedsChildRange) && !isNewMapping;
                                                    const parentBadgeLabel = isNewMapping ? 'New' : isPromotedParent ? 'Promoted' : '';
                                                    const hasParentBadge = isParent && !!parentBadgeLabel;
                                                    const qboDisplayName = isChild
                                                        ? String(fields[index].name || '').split(':').pop()
                                                        : fields[index].name;

                                                    const tintedSelectStyle = (backgroundColor: string, borderColor: string, glow: string) => ({
                                                        ...modernSelectStyle,
                                                        control: (provided: any, state: any) => ({
                                                            ...modernSelectStyle.control(provided, state),
                                                            backgroundColor,
                                                            borderColor: state.isFocused ? '#6366f1' : borderColor,
                                                            boxShadow: state.isFocused ? '0 0 0 3px rgba(99, 102, 241, 0.15)' : `0 0 0 1px ${glow}`,
                                                        }),
                                                    });
                                                    const rowSelectStyle = isNewMapping
                                                        ? tintedSelectStyle('#f5f3ff', '#c4b5fd', 'rgba(124, 58, 237, 0.25)')
                                                        : isMatchedMapping
                                                            ? tintedSelectStyle('#ecfdf5', '#a7f3d0', 'rgba(16, 185, 129, 0.25)')
                                                            : (needsReviewMapping || isFallbackMapping)
                                                                ? tintedSelectStyle('#fffbeb', '#fcd34d', 'rgba(245, 158, 11, 0.25)')
                                                                : modernSelectStyle;
                                                    const rowStyle = isFallbackMapping
                                                        ? { backgroundColor: '#fffdf5' }
                                                        : isBank
                                                            ? { backgroundColor: '#fffdf5' }
                                                            : isNewMapping
                                                                ? { backgroundColor: '#faf9fe' }
                                                                : isMatchedMapping
                                                                    ? { backgroundColor: '#f4fbf7' }
                                                                    : {};
                                                    return (
                                                        <React.Fragment key={data['id']}>
                                                            {startsSection && (
                                                                <tr className="qbo-section-strip">
                                                                    <th colSpan={6} style={{ top: stripTop }}>
                                                                        <div className="qbo-section-banner">
                                                                            <div className="qbo-section-title">
                                                                                <span className="qbo-section-icon">{getSectionIcon(sectionName)}</span>
                                                                                <span className="qbo-section-heading">{t(`classification.${sectionName}`, { defaultValue: sectionName })}</span>
                                                                            </div>
                                                                            {sectionSummary && (
                                                                                <div className="qbo-section-pills">
                                                                                    <span className="qbo-section-badge count">{t('section.accountCount', { count: sectionSummary.total })}</span>
                                                                                    {(sectionSummary.matched > 0) && (
                                                                                        <span className="qbo-section-badge matched">{t('section.matched', { count: sectionSummary.matched })}</span>
                                                                                    )}
                                                                                    {(sectionSummary.created > 0) && (
                                                                                        <span className="qbo-section-badge new">{t('section.new', { count: sectionSummary.created })}</span>
                                                                                    )}
                                                                                    {(sectionSummary.blank > 0) && (
                                                                                        <span className="qbo-section-badge unmapped">{t('section.unmapped', { count: sectionSummary.blank })}</span>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </th>
                                                                </tr>
                                                            )}
                                                            <tr
                                                                className={`align-middle h-60${isParent ? ' qbo-parent-row' : ''}${isChild ? ` qbo-child-row qbo-child-depth-${depth}` : ''}`}
                                                                style={rowStyle}
                                                            >
                                                                <td className="text-center" style={{ width: 45 }}>
                                                                    {isBank && (
                                                                        <div className="form-check d-flex justify-content-center" title={t('tooltip.title')}>
                                                                            <input
                                                                                className="form-check-input wh-20 checkbox"
                                                                                type="checkbox"
                                                                                {...register(`coa.${index}.checked`)}
                                                                                defaultChecked={fields[index]['checked']}
                                                                                onClick={() => bankSelectionError && setBankSelectionError(false)}
                                                                                style={{
                                                                                    border: bankSelectionError ? '2px solid #dc3545' : '1.5px solid #adb5bd',
                                                                                    boxShadow: bankSelectionError ? '0 0 0 2px rgba(220, 53, 69, 0.25)' : 'none',
                                                                                    cursor: 'pointer',
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td className="text-start qbo-account-cell" style={{ width: '25%' }}>
                                                                    {isChild ? (
                                                                        <span className={`${hierarchyNodeClass} qbo-child-name`} style={{ marginLeft: `${Math.max(depth - 1, 0) * 20}px` }}>
                                                                            <HierarchyConnector />
                                                                            <span className="qbo-child-label">
                                                                                {qboDisplayName}
                                                                            </span>
                                                                        </span>
                                                                    ) : (
                                                                        <strong>{qboDisplayName}</strong>
                                                                    )}
                                                                    {isParent && <span className="badge bg-secondary ms-2">{t('badge.parent')}</span>}
                                                                </td>
                                                                <td className="text-start text-truncate" style={{ width: '15%', maxWidth: '200px' }}>{fields[index].accountType || t('table.notAvailable')}</td>
                                                                <td className="text-start" style={{ width: '15%' }}>
                                                                    {isMatchedMapping && (
                                                                        <span className="qbo-status-pill matched">
                                                                            <i className="fas fa-check-circle" aria-hidden="true"></i> {t('status.matched')}
                                                                        </span>
                                                                    )}
                                                                    {isNewMapping && (
                                                                        <span className="qbo-status-pill new">
                                                                            <i className="fas fa-plus-circle" aria-hidden="true"></i> {t('status.autoCreate')}
                                                                        </span>
                                                                    )}
                                                                    {needsReviewMapping && (
                                                                        <span className="qbo-status-pill review">
                                                                            <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> {t('status.needsReview')}
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td className="text-start qbo-revrex-cell" style={{ width: '33%' }}>
                                                                    <div className={isChild ? `${hierarchyNodeClass} qbo-revrex-child-mapping` : ''} style={isChild ? { marginLeft: `${Math.max(depth - 1, 0) * 20}px` } : undefined}>
                                                                        {isChild && <HierarchyConnector />}
                                                                        <Form.Group
                                                                            ref={(el) => (dropdownRowRefs.current[index] = el)}
                                                                            className="col-sm-12 d-flex"
                                                                            controlId="formBasicEmail"
                                                                        >
                                                                            <Controller
                                                                                control={control}
                                                                                name={`coa.${index}.account_id`}
                                                                                render={({ field: { value, onChange } }) => (
                                                                                    isGeneratedPreview ? (
                                                                                        <div className="qbo-generated-preview" title={selectedPath || selectedOption?.label || ''}>
                                                                                            <i className="fas fa-plus-circle" aria-hidden="true"></i>
                                                                                            <span>{formatSelectedAccount(value, { context: 'value' })}</span>
                                                                                        </div>
                                                                                    ) : isParent ? (
                                                                                        <AsyncPaginate
                                                                                            selectRef={(el: any) => (selectRefs.current[index] = el as SelectInstance<any, false, any>)}
                                                                                            isClearable={true}
                                                                                            placeholder={t("select.placeholder")}
                                                                                            cacheUniqs={[parentAccountsLoaded, JSON.stringify(coaValues)]}
                                                                                            loadOptions={(searchText: string) => filterParentAccountList(searchText, index, value?.value || null)}
                                                                                            additional={{ page: 1 }}
                                                                                            onChange={(selection: any) => handleParentAccountChange(index, selection, onChange)}
                                                                                            value={value}
                                                                                            styles={rowSelectStyle}
                                                                                            formatOptionLabel={formatSelectedAccount}
                                                                                            components={{ Option: HierarchyOption }}
                                                                                            debounceTimeout={300}
                                                                                            menuPortalTarget={document.body}
                                                                                            menuPosition="fixed"
                                                                                        />
                                                                                    ) : (
                                                                                        <AsyncPaginate
                                                                                            selectRef={(el: any) => (selectRefs.current[index] = el as SelectInstance<any, false, any>)}
                                                                                            isClearable={true}
                                                                                            placeholder={t("select.placeholder")}
                                                                                            cacheUniqs={[accountListVersion, ...Array.from(getSelectedAccountIds(index))]}
                                                                                            loadOptions={(searchText: string, prevOptions: readonly any[], additional: any) =>
                                                                                                filterAccountList(
                                                                                                    searchText,
                                                                                                    prevOptions as any[],
                                                                                                    additional,
                                                                                                    fields[index].classification as ClassificationType,
                                                                                                    fields[index].accountType,
                                                                                                    index,
                                                                                                    value?.value || null,
                                                                                                    fields[index].name || ''
                                                                                                )
                                                                                            }
                                                                                            additional={{ page: 1 }}
                                                                                            onChange={(e: any) => {
                                                                                                if (e && getSelectedAccountIds(index).has(+e.value)) {
                                                                                                    setError(`coa.${index}.account_id`, {
                                                                                                        type: 'manual',
                                                                                                        message: t('duplicate.error'),
                                                                                                    });
                                                                                                    toast.error(t("duplicate.error"));
                                                                                                    return;
                                                                                                }

                                                                                                clearRowAccountError(index);
                                                                                                onChange(e);
                                                                                                setValue(`coa.${index}.mapping_source`, e ? 'manual' : null);
                                                                                                if (validationActivated) {
                                                                                                    advanceToNextInvalidDropdown(index);
                                                                                                } else {
                                                                                                    handleSelectChange(index, e);
                                                                                                }
                                                                                            }}
                                                                                            isOptionDisabled={(option: any) => {
                                                                                                if (value?.value && (+value.value === +option.value)) {
                                                                                                    return false;
                                                                                                }

                                                                                                return getSelectedAccountIds(index).has(+option.value);
                                                                                            }}
                                                                                            className='vp-dropdown'
                                                                                            menuPortalTarget={document.body}
                                                                                            menuPosition='fixed'
                                                                                            value={value}
                                                                                            defaultValue={value}
                                                                                            styles={rowSelectStyle}
                                                                                            formatOptionLabel={formatSelectedAccount}
                                                                                            components={{ Option: HierarchyOption, MenuList: CreateAccountMenuList(index) }}
                                                                                            debounceTimeout={300}
                                                                                        />
                                                                                    )
                                                                                )}
                                                                            />
                                                                        </Form.Group>
                                                                    </div>
                                                                    {
                                                                        (errors && errors.coa && errors.coa[index] && errors.coa[index]?.account_id && errors.coa[index]?.account_id?.message && (
                                                                            <Form.Text className="text-danger d-flex">
                                                                                <strong>
                                                                                    {errors.coa[index]?.account_id?.message as ReactNode || ''}
                                                                                </strong>
                                                                            </Form.Text>
                                                                        )) || ''
                                                                    }
                                                                    {
                                                                        (sectionMismatch && (
                                                                            <Form.Text className="qbo-section-warning d-flex">
                                                                                <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>&nbsp;
                                                                                {t('coa.sectionMismatchWarning', {
                                                                                    type: sectionMismatch.type,
                                                                                    parents: sectionMismatch.parents,
                                                                                })}
                                                                            </Form.Text>
                                                                        )) || ''
                                                                    }
                                                                </td>
                                                                <td className="text-start qbo-action-cell" style={{ width: '12%' }}>
                                                                    <div className="qbo-revrex-path">
                                                                        {isParent && selectedOption?.value && (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-secondary btn-sm"
                                                                                onClick={() => handleOpenParentDetails(index)}
                                                                            >
                                                                                <i className="fas fa-edit me-1" aria-hidden="true"></i> {t('btn.edit')}
                                                                            </button>
                                                                        )}
                                                                        {needsReviewMapping && !hasParentBadge && !(isParent && selectedOption?.value) && (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-primary btn-sm"
                                                                                onClick={() => handleOpenCreateModal(index)}
                                                                            >
                                                                                <i className="fas fa-plus me-1" aria-hidden="true"></i> {t('btn.createGL')}
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        </React.Fragment>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan={6} className="text-center py-5">
                                                        <div className="d-flex flex-column align-items-center justify-content-center gap-3 py-3">
                                                            <div className="bg-primary-subtle text-primary p-3 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '64px', height: '64px' }}>
                                                                <i className="fas fa-sitemap fa-2x"></i>
                                                            </div>
                                                            <div>
                                                                <h5 className="font-weight-700 text-dark mb-1">{t('empty.title')}</h5>
                                                                <p className="text-muted small mb-0" style={{ maxWidth: '480px' }}>
                                                                    {t('empty.description')}
                                                                </p>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="btn btn-primary px-4 py-2.5 rounded-pill font-weight-600 shadow-sm d-inline-flex align-items-center gap-2 mt-2"
                                                                onClick={handleImportCOA}
                                                                disabled={isImporting || isLoading}
                                                            >
                                                                {isImporting ? (
                                                                    <>
                                                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                                                        <span>{t('empty.importing')}</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <i className="fas fa-cloud-download-alt"></i>
                                                                        <span>{t('empty.import')}</span>
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })()}
                                    </tbody>
                                </table>
                            </div>
                            {((autoMatchedCount > 0) || (autoCreatedCount > 0)) && (
                                <div className="qbo-first-valid-review mt-3">
                                    {(autoMatchedCount > 0) && (
                                        <Form.Check
                                            type="checkbox"
                                            id="qbo-auto-mapping-review"
                                            checked={autoMappingsReviewed}
                                            onChange={(e) => setAutoMappingsReviewed(e.target.checked)}
                                            label={t('mapping.reviewConfirmation', { count: autoMatchedCount })}
                                        />
                                    )}
                                    {(autoCreatedCount > 0) && (
                                        <div className="text-muted small mt-1">
                                            {t('mapping.createdSummary', { count: autoCreatedCount })}
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className='text-end mt-3'>
                                {(fields.length > 0) && renderVerifySubmitButton(false)}
                            </div>
                        </Form>
                    </div>
                </div>
            </div>
            {showCreateModal && (
                <ChartAccountModal
                    closeModal={handleCloseCreateModal}
                    addRecord={!parentDetailsRecord}
                    record={parentDetailsRecord}
                    sshSection={createModalSection}
                    parentAccountIn={createModalParentAccountIn}
                    prefillName={createModalAccountName}
                    preselectFirstAccount={true}
                    metadataOnly={!!parentDetailsRecord}
                    metadataAccountOptions={parentDetailsAccountOptions}
                    forcedParentId={createModalForcedParentId}
                    usedAccountNumbers={hierarchyAccountsRef.current.map(account => account.accountNum)}
                />
            )}
            {(pendingPromotionConfirm?.promotions?.length > 0) && (
                <Modal show onHide={handlePromotionCancel} backdrop="static" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>{t('promotion.title')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>
                            {t('promotion.summary', { count: pendingPromotionConfirm.promotions.length })}
                        </p>
                        <ul className="mb-0">
                            {pendingPromotionConfirm.promotions.map((p: any) => (
                                <li key={p.account_id}>
                                    <strong>{p.qbo_name}</strong> — {t('promotion.itemDetail', { count: p.children_account_ids?.length || 0 })}
                                </li>
                            ))}
                        </ul>
                    </Modal.Body>
                    <Modal.Footer>
                        <button type="button" className="btn btn-outline-secondary" onClick={handlePromotionCancel}>
                            {t('buttons.cancel')}
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handlePromotionConfirm}>
                            {t('buttons.continue')}
                        </button>
                    </Modal.Footer>
                </Modal>
            )}
            {showBankRegModal && (
                <QBBankRegistrationModal
                    rows={pendingBankRows}
                    branchId={+branchData['id']}
                    onCancel={handleBankRegCancel}
                    onConfirm={handleBankRegConfirm}
                />
            )}
            {showReconciliationCutoffModal && (
                <QBDReconciliationCutoffModal
                    branchId={+branchData['id']}
                    onDone={() => {
                        setShowReconciliationCutoffModal(false);
                        navigate(-1);
                    }}
                />
            )}
        </div>
    )
}

export default QBCOAClassification;
